import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { BarChart3, Download, Users, MapPin, Building2, Send, Calendar, Loader2, RefreshCw, Trash2, Pencil, Check, X, FileBarChart, ClipboardList, Hammer, BadgeCheck, UserPlus, UsersRound, HardHat, Printer, Upload, Maximize2, Minimize2, CalendarDays, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PayRosterBulkModal from "../components/PayRosterBulkModal";
import DirectoryPage from "./DirectoryPage";
import StateWeather from "../components/dashboard/StateWeather";
import { EventsReportPanel } from "./AdminDashboardPage";
import WorkmenApprovalsPage from "./WorkmenApprovalsPage";
import ContractorApprovalsPage from "./ContractorApprovalsPage";
import PayRosterApprovalsPage from "./PayRosterApprovalsPage";

const TABS = [
  { id: "summary", label: "Executive Summary", icon: <FileBarChart className="h-4 w-4" /> },
  { id: "approvals", label: "Approval Queues", icon: <BadgeCheck className="h-4 w-4" /> },
  { id: "registration-details", label: "Manpower Registration Details", icon: <ClipboardList className="h-4 w-4" /> },
  { id: "contractor-ncc", label: "Contractor NCC Workforce", icon: <Hammer className="h-4 w-4" /> },
  { id: "new-contractors", label: "New Contractors (Non-NCC)", icon: <UserPlus className="h-4 w-4" /> },
  { id: "workmen-roster", label: "Workmen-Pay Roster", icon: <UsersRound className="h-4 w-4" /> },
  { id: "statutory", label: "Statutory Compliance", icon: <ShieldCheck className="h-4 w-4" /> },
  { id: "ready", label: "Ready to Mobilize / Join", icon: <Send className="h-4 w-4" /> },
  { id: "ncc-sites", label: "NCC Sites", icon: <Building2 className="h-4 w-4" /> },
  { id: "workforce", label: "Workforce", icon: <Users className="h-4 w-4" /> },
  { id: "funnel", label: "Registration Funnel", icon: <BarChart3 className="h-4 w-4" /> },
  { id: "events", label: "Events · Weather", icon: <CalendarDays className="h-4 w-4" /> },
  { id: "directory", label: "Directory", icon: <HardHat className="h-4 w-4" /> },
];

function NccWorkforceTab() {
  const [view, setView] = useState("contractor");
  return (
    <div data-testid="ncc-workforce-tab">
      <ReportSubToggle
        value={view}
        onChange={setView}
        options={[
          { id: "contractor", label: "Contractor · NCC Workforce Details" },
          { id: "payroll", label: "Payroll Manpower · NCC Workforce Details" },
        ]}
        testidPrefix="ncc-workforce-toggle"
      />
      {view === "contractor" ? <ContractorNCCWorkforce /> : <PayrollNCCWorkforce />}
    </div>
  );
}

function NewNonNccTab() {
  const [view, setView] = useState("contractor");
  return (
    <div data-testid="new-non-ncc-tab">
      <ReportSubToggle
        value={view}
        onChange={setView}
        options={[
          { id: "contractor", label: "New Contractors (Non-NCC)" },
          { id: "payroll", label: "New Payroll Manpower (Non-NCC)" },
        ]}
        testidPrefix="new-non-ncc-toggle"
      />
      {view === "contractor" ? <NewContractorsReport /> : <NewPayrollReport />}
    </div>
  );
}

function ReportSubToggle({ value, onChange, options, testidPrefix }) {
  return (
    <div className="mb-4 flex flex-wrap gap-2 border-2 border-slate-900 bg-white p-2 shadow-brutal-sm" data-testid={testidPrefix}>
      {options.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            data-testid={`${testidPrefix}-${o.id}`}
            className={`flex-1 min-w-[180px] border-2 px-3 py-2 industrial-label text-[11px] transition-all sm:text-sm ${
              active
                ? "border-ncc-navy bg-ncc-navy text-white shadow-brutal-sm"
                : "border-slate-300 bg-white text-slate-700 hover:border-ncc-navy hover:text-ncc-navy"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function ReportsPage() {
  const { user, loading, can } = useAuth();
  const [tab, setTab] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("tab") || "summary"; } catch { return "summary"; }
  });
  const [fullscreen, setFullscreen] = useState(false);

  // Strict role-based tab visibility — each tab lists the permissions that
  // unlock it (any-of). Super admins see everything via can().
  const TAB_ACCESS = {
    summary: ["view_stats"],
    approvals: ["approve_contractors", "approve_payroll", "approve_workmen", "approve_payroll_roster", "approve_medical"],
    "registration-details": ["report_manpower_registration"],
    "contractor-ncc": ["report_contractor_workforce", "report_payroll_workforce"],
    "new-contractors": ["report_new_contractors", "report_new_payroll"],
    "workmen-roster": ["workmen_roster_report"],
    statutory: ["report_statutory"],
    ready: ["report_ready_mobilize"],
    "ncc-sites": ["report_ncc_sites"],
    workforce: ["report_workforce"],
    funnel: ["report_funnel"],
    events: ["view_stats"],
    directory: ["report_directory"],
  };
  const visibleTabs = useMemo(
    () => TABS.filter((t) => {
      const req = TAB_ACCESS[t.id];
      return !req || req.some((p) => can(p));
    }),
    [can], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Never render a tab the user is not permitted to see — fall back to the
  // first visible tab (guards the initial "summary" default for restricted admins).
  const activeTab = visibleTabs.find((t) => t.id === tab) ? tab : (visibleTabs[0]?.id || null);
  // If the active tab gets gated away (e.g. role permissions changed), fall back to the first visible tab.
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.find((t) => t.id === tab)) setTab(visibleTabs[0].id);
  }, [visibleTabs, tab]);

  // Esc key exits fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  // Hide the global Navbar (and any sticky chrome) while fullscreen is active.
  // The Navbar listens for `body.reports-fullscreen-active` via a MutationObserver
  // and returns null when present, giving a true edge-to-edge experience.
  useEffect(() => {
    document.body.classList.toggle("reports-fullscreen-active", fullscreen);
    return () => document.body.classList.remove("reports-fullscreen-active");
  }, [fullscreen]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className={fullscreen ? "px-2 py-3" : "mx-auto max-w-[1500px] px-4 py-10 md:px-8"} data-testid="reports-page">
      {!fullscreen && (
        <div>
          <div className="industrial-label text-ncc-gold2">// Reports</div>
          <h1 className="mt-2 font-heading text-3xl font-black uppercase tracking-tight text-slate-900 md:text-5xl">Reports</h1>
          <p className="mt-2 max-w-3xl font-mono text-sm text-slate-600">Workforce strength, registration funnel, NCC site loadout, and mobilization readiness — with date filters and Excel export for every report.</p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setFullscreen((v) => !v)}
          data-testid="reports-fullscreen-toggle"
          className="inline-flex items-center gap-1.5 rounded-md border-2 border-ncc-navy bg-white px-3 py-1.5 industrial-label text-xs text-ncc-navy hover:bg-ncc-tint"
          title={fullscreen ? "Exit fullscreen (Esc)" : "View in fullscreen"}
        >
          {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          {fullscreen ? "Exit Fullscreen" : "Fullscreen"}
        </button>
      </div>

      <div className={`flex flex-col gap-6 ${fullscreen ? "mt-2 lg:flex-row" : "mt-8 lg:flex-row"}`} data-testid="reports-layout">
        {/* Sidebar — hidden in fullscreen */}
        {!fullscreen && (
        <aside className="lg:w-64 lg:shrink-0" aria-label="Reports navigation">
          <div className="lg:sticky lg:top-24 border-2 border-slate-900 bg-white shadow-brutal-sm">
            <div className="border-b-2 border-slate-900 bg-ncc-navy px-3 py-2.5">
              <div className="industrial-label text-[10px] text-ncc-gold2">// Sections</div>
              <div className="mt-0.5 font-heading text-base font-black text-white">Report Modules</div>
            </div>
            <nav className="flex flex-col p-2" data-testid="reports-sidebar">
              {visibleTabs.map((t) => {
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTab(t.id)}
                    data-testid={`reports-tab-${t.id}`}
                    className={`flex items-center gap-2.5 border-l-4 px-3 py-2.5 industrial-label text-left transition-all ${
                      active
                        ? "border-ncc-gold bg-ncc-navy text-white shadow-brutal-sm"
                        : "border-transparent text-slate-700 hover:border-ncc-navy/40 hover:bg-slate-100"
                    }`}
                  >
                    <span className={active ? "text-ncc-gold2" : "text-ncc-navy"}>{t.icon}</span>
                    <span className="flex-1">{t.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>
        )}

        {/* Main content */}
        <div className="min-w-0 flex-1 reports-content">
          <ReportActions tab={activeTab} onJumpDirectory={() => setTab("directory")} />
          {activeTab === "summary" && <ExecutiveSummaryReport />}
          {activeTab === "workforce" && <WorkforceReport />}
          {activeTab === "funnel" && <FunnelReport />}
          {activeTab === "events" && <EventsReportPanel />}
          {activeTab === "approvals" && <ApprovalQueuesPanel />}
          {activeTab === "ncc-sites" && <NccSitesReport />}
          {activeTab === "ready" && <ReadyReport />}
          {activeTab === "registration-details" && <ManpowerRegistrationDetails />}
          {activeTab === "contractor-ncc" && <NccWorkforceTab />}
          {activeTab === "new-contractors" && <NewNonNccTab />}
          {activeTab === "workmen-roster" && <WorkmenRosterTab />}
          {activeTab === "statutory" && <StatutoryComplianceTab />}
          {activeTab === "payments" && <PaymentsReportTab />}
          {activeTab === "directory" && can("report_directory") && <DirectoryPage />}
        </div>
      </div>
    </div>
  );
}

// ---------- 0. Executive Summary ----------
function ExecutiveSummaryReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const r = await api.get("/reports/manpower-summary", { params });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const rows = data?.rows || [];
  const cardColors = {
    Contractor: "bg-blueprint text-white",
    "Payroll Manpower": "bg-ncc-tint text-ncc-navy border-ncc-navy",
    "Grand Total": "bg-ncc-gold text-ncc-navy border-ncc-navy",
  };

  return (
    <div data-testid="exec-summary-report">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} onClear={() => { setFrom(""); setTo(""); }} />
      </div>

      {/* KPI Cards */}
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {rows.map((r) => (
          <div
            key={r.category}
            data-testid={`summary-card-${r.category.toLowerCase().replace(/\s+/g, "-")}`}
            className={`border-2 ${cardColors[r.category] || "bg-white border-slate-900"} p-5 shadow-brutal-sm`}
          >
            <div className="industrial-label opacity-80">{r.category}</div>
            <div className="mt-3 grid grid-cols-2 gap-3 font-mono text-xs">
              <div><div className="opacity-70">Registered</div><div className="font-heading text-2xl font-black">{r.registered.toLocaleString()}</div></div>
              <div><div className="opacity-70">Total Workforce Capacity</div><div className="font-heading text-2xl font-black">{r.total_crew.toLocaleString()}</div></div>
              <div><div className="opacity-70">Current NCC Workforce</div><div className="font-heading text-2xl font-black">{r.working.toLocaleString()}</div></div>
              <div><div className="opacity-70">Mobilization Reserve</div><div className="font-heading text-2xl font-black">{r.available.toLocaleString()}</div></div>
            </div>
            <div className="mt-3 border-t border-current/30 pt-2 font-mono text-xs">
              Deployment{" "}
              <span className="ml-1 inline-block rounded bg-white/30 px-1.5 font-heading text-base font-black">{r.deployment_pct}%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Detail table */}
      <div className="mt-8 border-2 border-slate-900 bg-white">
        <div className="border-b-2 border-slate-900 bg-ncc-navy px-4 py-3">
          <div className="industrial-label text-ncc-gold2">// Executive Summary</div>
          <h3 className="mt-0.5 font-heading text-lg font-black text-white">Manpower Registration &amp; NCC Deployment</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-4 py-3 text-left industrial-label">Category</th>
              <th className="px-4 py-3 text-right industrial-label">Registered</th>
              <th className="px-4 py-3 text-right industrial-label">Total Workforce Capacity</th>
              <th className="px-4 py-3 text-right industrial-label">Current NCC Workforce</th>
              <th className="px-4 py-3 text-right industrial-label">Mobilization Reserve</th>
              <th className="px-4 py-3 text-right industrial-label">Deployment %</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-6 text-center font-mono text-slate-500">
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.category} className={`border-t border-slate-200 ${r.is_total ? "bg-ncc-gold/20 font-bold" : "hover:bg-slate-50"}`} data-testid={`summary-row-${r.category.toLowerCase().replace(/\s+/g, "-")}`}>
                <td className="px-4 py-3 font-heading text-slate-900">{r.category}</td>
                <td className="px-4 py-3 text-right font-mono">{r.registered.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-mono">{r.total_crew.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-700">{r.working.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-mono text-blueprint">{r.available.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-mono font-black text-slate-900">{r.deployment_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t-2 border-slate-900 bg-slate-50 px-4 py-2 font-mono text-[11px] text-slate-500">
          Counts include <b>approved</b> records only. &ldquo;Current NCC Workforce&rdquo; = records with status <b>NCCL</b>. &ldquo;Mobilization Reserve&rdquo; = Total Workforce Capacity − Current NCC Workforce.
        </div>
      </div>

      {/* Workmen Roster Status (site-wise) */}
      <WorkmenRosterStatusBlock />

      {/* Payroll Roster Status (site-wise) — sibling of the workmen block */}
      <PayrollRosterStatusBlock />

      {/* Contractor-Site Wise Roster */}
      <ContractorSiteRosterBlock />

      {/* Contractor-Wise Reserve Roster — separate tables for Workmen & Payroll */}
      <ContractorReserveRosterBlock payroll={false} />
      <ContractorReserveRosterBlock payroll={true} />
    </div>
  );
}

// Computes Current NCC Workforce + Workmen Rosters per (Site) and per (Contractor × Site)
// for BOTH Contractors and Payroll Manpower. Each row also exposes a "registered"
// count = number of approved records with that site assigned, so the reader can
// reconcile against the Exec Summary "Registered" column.
function useRosterAggregates() {
  const [rows, setRows] = useState({ bySite: [], byContractorSite: [], byPayrollSite: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/contractors", { params: { include_pending: true } }),
      api.get("/workmen/all"),
    ])
      .then(([cr, wr]) => {
        const all = (cr.data || []).filter((c) => c.status === "approved");
        const contractors = all.filter((c) => !c.is_payroll);
        const payroll = all.filter((c) => !!c.is_payroll);
        const workmen = wr.data || [];

        // Helper: list of (site, nos) for a contractor — prefers per-site allocations, else legacy ready_nos onto sites
        function sitesNosOf(c) {
          const isNccl = (c.current_status || "").toUpperCase() === "NCCL";
          if (!isNccl) return [];
          const alloc = c.ncc_site_allocations || {};
          const ss = Array.isArray(c.ncc_site_names) && c.ncc_site_names.length
            ? c.ncc_site_names
            : (c.ncc_site_name ? c.ncc_site_name.split(",").map((s) => s.trim()).filter(Boolean) : []);
          if (Object.keys(alloc).length > 0) {
            return ss.map((s) => [s, Number(alloc[s] || 0)]);
          }
          const totalNos = Number(c.ready_nos || c.crew_size || 0);
          if (ss.length === 0) return [];
          if (ss.length === 1) return [[ss[0], totalNos]];
          // Spread evenly across sites when no explicit allocation
          const per = Math.floor(totalNos / ss.length);
          const rem = totalNos - per * ss.length;
          return ss.map((s, i) => [s, per + (i === 0 ? rem : 0)]);
        }

        // Helper: list of sites a contractor is REGISTERED against (any current_status —
        // NCCL or NEW), independent of crew allocation. Used for the "Registered" column.
        function sitesOf(c) {
          const ss = Array.isArray(c.ncc_site_names) && c.ncc_site_names.length
            ? c.ncc_site_names
            : (c.ncc_site_name ? c.ncc_site_name.split(",").map((s) => s.trim()).filter(Boolean) : []);
          return ss.filter(Boolean);
        }

        // Workmen counts: only ACTIVE + APPROVED contribute to "Workmen Rosters"
        const rosterBySiteAll = {};
        const rosterByContractorSite = {};
        const rosterBySitePayroll = {};
        const isPayrollById = {};
        payroll.forEach((c) => { isPayrollById[c.id] = true; });
        workmen.forEach((w) => {
          if (w.is_active === false) return;
          if ((w.status || "pending") !== "approved") return;
          const site = w.ncc_site_name || "";
          if (!site) return;
          if (isPayrollById[w.contractor_id]) {
            rosterBySitePayroll[site] = (rosterBySitePayroll[site] || 0) + 1;
          } else {
            rosterBySiteAll[site] = (rosterBySiteAll[site] || 0) + 1;
            const key = `${w.contractor_id}|${site}`;
            rosterByContractorSite[key] = (rosterByContractorSite[key] || 0) + 1;
          }
        });

        // Site-wise contractor registration counts (any approved Contractor with that site)
        const registeredBySiteContractor = {};
        contractors.forEach((c) => {
          sitesOf(c).forEach((site) => {
            registeredBySiteContractor[site] = (registeredBySiteContractor[site] || 0) + 1;
          });
        });

        // Build Contractor site-wise rows
        const siteAgg = {};
        contractors.forEach((c) => {
          sitesNosOf(c).forEach(([site, nos]) => {
            if (!site) return;
            siteAgg[site] = (siteAgg[site] || 0) + (Number(nos) || 0);
          });
        });
        const siteRows = Object.keys({ ...siteAgg, ...rosterBySiteAll, ...registeredBySiteContractor })
          .sort().map((site, i) => {
            const ncc = siteAgg[site] || 0;
            const roster = rosterBySiteAll[site] || 0;
            const registered = registeredBySiteContractor[site] || 0;
            return { srNo: i + 1, site, registered, ncc, roster, balance: ncc - roster };
          });

        // Build contractor × site rows
        const csRows = [];
        let n = 0;
        contractors.forEach((c) => {
          sitesNosOf(c).forEach(([site, nos]) => {
            const key = `${c.id}|${site}`;
            const roster = rosterByContractorSite[key] || 0;
            csRows.push({
              srNo: ++n,
              contractor: c.name,
              type: c.contractor_type || "—",
              site,
              ncc: Number(nos) || 0,
              roster,
              balance: (Number(nos) || 0) - roster,
            });
          });
        });
        csRows.sort((a, b) => (a.contractor + a.site).localeCompare(b.contractor + b.site));
        csRows.forEach((r, i) => { r.srNo = i + 1; });

        // Payroll Manpower site-wise rows — mirrors siteRows but for is_payroll=true contractors.
        // For payroll, "Registered" = count of payroll records assigned to the site,
        // "Current Payroll Workforce" = sum of crew_size when current_status=NCCL,
        // "Pay Rosters" = count of approved+active workmen rows tied to a payroll contractor at that site.
        const payrollSiteCrewAgg = {};
        const payrollRegisteredBySite = {};
        payroll.forEach((c) => {
          sitesOf(c).forEach((site) => {
            payrollRegisteredBySite[site] = (payrollRegisteredBySite[site] || 0) + 1;
          });
          sitesNosOf(c).forEach(([site, nos]) => {
            if (!site) return;
            payrollSiteCrewAgg[site] = (payrollSiteCrewAgg[site] || 0) + (Number(nos) || 0);
          });
        });
        const payrollSiteRows = Object.keys({ ...payrollSiteCrewAgg, ...rosterBySitePayroll, ...payrollRegisteredBySite })
          .sort().map((site, i) => {
            const ncc = payrollSiteCrewAgg[site] || 0;
            const roster = rosterBySitePayroll[site] || 0;
            const registered = payrollRegisteredBySite[site] || 0;
            return { srNo: i + 1, site, registered, ncc, roster, balance: ncc - roster };
          });

        setRows({ bySite: siteRows, byContractorSite: csRows, byPayrollSite: payrollSiteRows });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load roster aggregates"))
      .finally(() => setLoading(false));
  }, []);
  return { ...rows, loading };
}

function WorkmenRosterStatusBlock() {
  const { bySite, loading } = useRosterAggregates();
  const totals = bySite.reduce(
    (a, r) => ({ registered: a.registered + r.registered, ncc: a.ncc + r.ncc, roster: a.roster + r.roster, balance: a.balance + r.balance }),
    { registered: 0, ncc: 0, roster: 0, balance: 0 },
  );
  return (
    <div className="mt-8 border-2 border-slate-900 bg-white" data-testid="workmen-roster-status-block">
      <div className="border-b-2 border-slate-900 bg-ncc-navy px-4 py-3">
        <div className="industrial-label text-ncc-gold2">// Workmen Roster Status</div>
        <h3 className="mt-0.5 font-heading text-lg font-black text-white">Site-wise NCC Workforce vs Roster Coverage</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left industrial-label">Sr No.</th>
              <th className="px-3 py-2 text-left industrial-label">Site</th>
              <th className="px-3 py-2 text-right industrial-label">Registered</th>
              <th className="px-3 py-2 text-right industrial-label">Current NCC Workforce</th>
              <th className="px-3 py-2 text-right industrial-label">Workmen Rosters</th>
              <th className="px-3 py-2 text-right industrial-label">Balance</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={6} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>)}
            {!loading && bySite.length === 0 && (<tr><td colSpan={6} className="p-6 text-center font-mono text-slate-500">No NCC sites with workforce yet.</td></tr>)}
            {!loading && bySite.map((r) => (
              <tr key={r.site} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`wrs-site-${r.site.replace(/\s+/g, "-")}`}>
                <td className="px-3 py-2 font-mono text-[12px]">{r.srNo}</td>
                <td className="px-3 py-2 font-bold text-slate-900">{r.site}</td>
                <td className="px-3 py-2 text-right font-mono">{r.registered.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{r.ncc.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono">{r.roster.toLocaleString()}</td>
                <td className={`px-3 py-2 text-right font-mono font-black ${r.balance < 0 ? "text-red-700" : r.balance === 0 ? "text-emerald-700" : "text-blueprint"}`}>{r.balance.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          {bySite.length > 0 && (
            <tfoot className="bg-ncc-gold/30">
              <tr className="font-bold">
                <td colSpan={2} className="px-3 py-3 industrial-label text-ncc-navy">GRAND TOTAL</td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-slate-900">{totals.registered.toLocaleString()}</td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-emerald-800">{totals.ncc.toLocaleString()}</td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-ncc-navy">{totals.roster.toLocaleString()}</td>
                <td className={`px-3 py-3 text-right font-heading text-base font-black ${totals.balance < 0 ? "text-red-700" : "text-blueprint"}`}>{totals.balance.toLocaleString()}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="border-t-2 border-slate-900 bg-slate-50 px-4 py-2 font-mono text-[11px] text-slate-500">
        <b>Registered</b> = approved Contractors with this site assigned. <b>Balance</b> = Current NCC Workforce − Workmen Rosters. Negative = workman over-roster vs allocation; positive = roster still to be filled.
      </div>
    </div>
  );
}

// Payroll sibling of WorkmenRosterStatusBlock — same shape, but the source records
// come from is_payroll=true contractors and the roster column counts approved + active
// workmen tied to a payroll contractor at that site.
function PayrollRosterStatusBlock() {
  const { byPayrollSite, loading } = useRosterAggregates();
  const totals = byPayrollSite.reduce(
    (a, r) => ({ registered: a.registered + r.registered, ncc: a.ncc + r.ncc, roster: a.roster + r.roster, balance: a.balance + r.balance }),
    { registered: 0, ncc: 0, roster: 0, balance: 0 },
  );
  return (
    <div className="mt-8 border-2 border-slate-900 bg-white" data-testid="payroll-roster-status-block">
      <div className="border-b-2 border-slate-900 bg-ncc-navy px-4 py-3">
        <div className="industrial-label text-ncc-gold2">// Payroll Roster Status</div>
        <h3 className="mt-0.5 font-heading text-lg font-black text-white">Site-wise Payroll Workforce vs Roster Coverage</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left industrial-label">Sr No.</th>
              <th className="px-3 py-2 text-left industrial-label">Site</th>
              <th className="px-3 py-2 text-right industrial-label">Registered</th>
              <th className="px-3 py-2 text-right industrial-label">Current Payroll Workforce</th>
              <th className="px-3 py-2 text-right industrial-label">Pay Rosters</th>
              <th className="px-3 py-2 text-right industrial-label">Balance</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={6} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>)}
            {!loading && byPayrollSite.length === 0 && (<tr><td colSpan={6} className="p-6 text-center font-mono text-slate-500">No Payroll Manpower records assigned to any NCC site yet.</td></tr>)}
            {!loading && byPayrollSite.map((r) => (
              <tr key={r.site} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`prs-site-${r.site.replace(/\s+/g, "-")}`}>
                <td className="px-3 py-2 font-mono text-[12px]">{r.srNo}</td>
                <td className="px-3 py-2 font-bold text-slate-900">{r.site}</td>
                <td className="px-3 py-2 text-right font-mono">{r.registered.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{r.ncc.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono">{r.roster.toLocaleString()}</td>
                <td className={`px-3 py-2 text-right font-mono font-black ${r.balance < 0 ? "text-red-700" : r.balance === 0 ? "text-emerald-700" : "text-blueprint"}`}>{r.balance.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          {byPayrollSite.length > 0 && (
            <tfoot className="bg-ncc-gold/30">
              <tr className="font-bold">
                <td colSpan={2} className="px-3 py-3 industrial-label text-ncc-navy">GRAND TOTAL</td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-slate-900">{totals.registered.toLocaleString()}</td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-emerald-800">{totals.ncc.toLocaleString()}</td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-ncc-navy">{totals.roster.toLocaleString()}</td>
                <td className={`px-3 py-3 text-right font-heading text-base font-black ${totals.balance < 0 ? "text-red-700" : "text-blueprint"}`}>{totals.balance.toLocaleString()}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="border-t-2 border-slate-900 bg-slate-50 px-4 py-2 font-mono text-[11px] text-slate-500">
        <b>Registered</b> = approved Payroll Manpower records with this site assigned. <b>Pay Rosters</b> = approved + active workmen rows of payroll contractors at this site.
      </div>
    </div>
  );
}

function ContractorSiteRosterBlock() {
  const { byContractorSite, loading } = useRosterAggregates();
  const totals = byContractorSite.reduce((a, r) => ({ ncc: a.ncc + r.ncc, roster: a.roster + r.roster, balance: a.balance + r.balance }), { ncc: 0, roster: 0, balance: 0 });
  return (
    <div className="mt-8 border-2 border-slate-900 bg-white" data-testid="contractor-site-roster-block">
      <div className="border-b-2 border-slate-900 bg-ncc-navy px-4 py-3">
        <div className="industrial-label text-ncc-gold2">// Contractor-Site Wise Roster</div>
        <h3 className="mt-0.5 font-heading text-lg font-black text-white">Per-Contractor × Site Roster Coverage</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left industrial-label">Sr No.</th>
              <th className="px-3 py-2 text-left industrial-label">Contractor</th>
              <th className="px-3 py-2 text-left industrial-label">Type of Contractor</th>
              <th className="px-3 py-2 text-left industrial-label">Site Name</th>
              <th className="px-3 py-2 text-right industrial-label">Current NCC Workforce</th>
              <th className="px-3 py-2 text-right industrial-label">Workmen Rosters</th>
              <th className="px-3 py-2 text-right industrial-label">Balance</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={7} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>)}
            {!loading && byContractorSite.length === 0 && (<tr><td colSpan={7} className="p-6 text-center font-mono text-slate-500">No contractor-site allocations yet.</td></tr>)}
            {!loading && byContractorSite.map((r, idx) => (
              <tr key={`${r.contractor}-${r.site}-${idx}`} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`csr-row-${idx}`}>
                <td className="px-3 py-2 font-mono text-[12px]">{r.srNo}</td>
                <td className="px-3 py-2 font-bold text-slate-900">{r.contractor}</td>
                <td className="px-3 py-2">{r.type}</td>
                <td className="px-3 py-2">{r.site}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{r.ncc.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono">{r.roster.toLocaleString()}</td>
                <td className={`px-3 py-2 text-right font-mono font-black ${r.balance < 0 ? "text-red-700" : r.balance === 0 ? "text-emerald-700" : "text-blueprint"}`}>{r.balance.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
          {byContractorSite.length > 0 && (
            <tfoot className="bg-ncc-gold/30">
              <tr className="font-bold">
                <td colSpan={4} className="px-3 py-3 industrial-label text-ncc-navy">GRAND TOTAL · {byContractorSite.length} row(s)</td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-emerald-800">{totals.ncc.toLocaleString()}</td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-ncc-navy">{totals.roster.toLocaleString()}</td>
                <td className={`px-3 py-3 text-right font-heading text-base font-black ${totals.balance < 0 ? "text-red-700" : "text-blueprint"}`}>{totals.balance.toLocaleString()}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// Contractor-wise Mobilization Reserve and Ready-to-Mobilize signal table.
// Rendered TWICE on the Executive Summary — once for Labour Contractors
// (is_payroll=false) and once for Payroll Manpower (is_payroll=true) so each
// segment has its own dedicated grand-total + ready-signal feed.
function ContractorReserveRosterBlock({ payroll = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/contractors", { params: { include_pending: true } })
      .then((r) => {
        const out = (r.data || [])
          .filter((c) => c.status === "approved")
          .filter((c) => payroll ? !!c.is_payroll : !c.is_payroll)
          .map((c) => {
            const capacity = Number(c.crew_size || 0);
            // Currently deployed = sum of NCC allocations if status=NCCL, else 0
            const isNccl = (c.current_status || "").toUpperCase() === "NCCL";
            let deployed = 0;
            if (isNccl) {
              const alloc = c.ncc_site_allocations || {};
              const allocSum = Object.values(alloc).reduce((s, v) => s + (Number(v) || 0), 0);
              deployed = allocSum > 0 ? allocSum : Number(c.ready_nos || capacity || 0);
            }
            const reserve = Math.max(capacity - deployed, 0);
            return {
              id: c.id,
              contractor: c.name || "—",
              type: c.is_payroll ? "Payroll Manpower" : (c.contractor_type || "Labour Contractor"),
              capacity,
              deployed,
              reserve,
              ready_nos: c.ready_nos ?? null,
              ready_date: c.ready_date || null,
              ready_signal_type: c.ready_signal_type || null,
              ready_responded: !!c.ready_responded,
              ready_signaled_at: c.ready_signaled_at || null,
              ncc_site: c.ncc_site_name || (Array.isArray(c.ncc_site_names) ? c.ncc_site_names.join(", ") : "") || "—",
            };
          })
          .sort((a, b) => b.reserve - a.reserve || a.contractor.localeCompare(b.contractor))
          .map((r, i) => ({ ...r, srNo: i + 1 }));
        setRows(out);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load reserve roster"))
      .finally(() => setLoading(false));
  }, [payroll]);

  const totals = rows.reduce(
    (a, r) => ({
      capacity: a.capacity + r.capacity,
      deployed: a.deployed + r.deployed,
      reserve: a.reserve + r.reserve,
      ready_nos: a.ready_nos + (r.ready_nos || 0),
    }),
    { capacity: 0, deployed: 0, reserve: 0, ready_nos: 0 },
  );

  const signalLabel = (t) =>
    t === "mobilize" ? "Ready to Mobilize" :
    t === "join" ? "Ready to Join" :
    "—";

  const headingPrefix = payroll ? "Payroll-Wise Reserve Roster" : "Contractor-Wise Reserve Roster";
  const subHeading = payroll
    ? "Per-Payroll × Mobilization Reserve × Ready to Mobilize"
    : "Per-Contractor × Mobilization Reserve × Ready to Mobilize";
  const blockTestId = payroll ? "payroll-reserve-roster-block" : "contractor-reserve-roster-block";
  const rowTestPrefix = payroll ? "prr" : "crr";
  const workforceLabel = payroll ? "Current Payroll Workforce" : "Current NCC Workforce";

  return (
    <div className="mt-8 border-2 border-slate-900 bg-white" data-testid={blockTestId}>
      <div className="border-b-2 border-slate-900 bg-ncc-navy px-4 py-3">
        <div className="industrial-label text-ncc-gold2">// {headingPrefix}</div>
        <h3 className="mt-0.5 font-heading text-lg font-black text-white">{subHeading}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left industrial-label">Sr No.</th>
              <th className="px-3 py-2 text-left industrial-label">{payroll ? "Payroll" : "Contractor"}</th>
              <th className="px-3 py-2 text-left industrial-label">Type</th>
              <th className="px-3 py-2 text-right industrial-label">Total Workforce Capacity</th>
              <th className="px-3 py-2 text-right industrial-label">{workforceLabel}</th>
              <th className="px-3 py-2 text-right industrial-label">Mobilization Reserve</th>
              <th className="px-3 py-2 text-left industrial-label">Ready Signal</th>
              <th className="px-3 py-2 text-right industrial-label">Ready Nos.</th>
              <th className="px-3 py-2 text-left industrial-label">Ready Date</th>
              <th className="px-3 py-2 text-left industrial-label">Admin Ack</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="p-6 text-center font-mono text-slate-500">
                {payroll ? "No approved Payroll Manpower records yet." : "No approved Labour Contractors yet."}
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`${rowTestPrefix}-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-[12px]">{r.srNo}</td>
                <td className="px-3 py-2 font-bold text-slate-900">{r.contractor}</td>
                <td className="px-3 py-2 font-mono text-[12px] text-slate-600">{r.type}</td>
                <td className="px-3 py-2 text-right font-mono">{r.capacity.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{r.deployed.toLocaleString()}</td>
                <td className={`px-3 py-2 text-right font-mono font-black ${r.reserve > 0 ? "text-blueprint" : "text-slate-400"}`}>
                  {r.reserve.toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  {r.ready_signal_type ? (
                    <span className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700">
                      {signalLabel(r.ready_signal_type)}
                    </span>
                  ) : (
                    <span className="font-mono text-[11px] text-slate-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono">{r.ready_nos != null ? r.ready_nos.toLocaleString() : "—"}</td>
                <td className="px-3 py-2 font-mono text-[12px]">
                  {r.ready_date ? new Date(r.ready_date).toLocaleDateString("en-GB") : "—"}
                </td>
                <td className="px-3 py-2">
                  {r.ready_signal_type ? (
                    r.ready_responded ? (
                      <span className="inline-flex items-center gap-1 rounded border border-emerald-500 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700">
                        Acknowledged
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded border border-amber-400 bg-amber-50 px-1.5 py-0.5 text-[11px] font-bold text-amber-700">
                        Pending
                      </span>
                    )
                  ) : (
                    <span className="font-mono text-[11px] text-slate-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-ncc-gold/30">
              <tr className="font-bold">
                <td colSpan={3} className="px-3 py-3 industrial-label text-ncc-navy">
                  GRAND TOTAL · {rows.length} {payroll ? "payroll record(s)" : "contractor(s)"}
                </td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-slate-900">{totals.capacity.toLocaleString()}</td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-emerald-800">{totals.deployed.toLocaleString()}</td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-blueprint">{totals.reserve.toLocaleString()}</td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3 text-right font-heading text-base font-black text-ncc-navy">{totals.ready_nos.toLocaleString()}</td>
                <td className="px-3 py-3"></td>
                <td className="px-3 py-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="border-t-2 border-slate-900 bg-slate-50 px-4 py-2 font-mono text-[11px] text-slate-500">
        <b>Mobilization Reserve</b> = Total Workforce Capacity − {workforceLabel} (deployed). <b>Ready Signal</b>, <b>Ready Nos.</b> and <b>Ready Date</b> are the latest values the {payroll ? "payroll record holder" : "contractor"} self-submitted from the portal. <b>Admin Ack</b> = whether an admin has acknowledged the signal.
      </div>
    </div>
  );
}

// ---------- 5. Manpower Registration Details (2×3 cross-tab) ----------
function ManpowerRegistrationDetails() {
  const [k, setK] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard?segment=all")
      .then((r) => setK(r.data.kpis))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !k) {
    return <div className="grid place-items-center py-16 font-mono text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const ec = k.nccl_workforce_contractor ?? 0;
  const ep = k.nccl_workforce_payroll ?? 0;
  const nc = k.new_registered_workforce_contractor ?? 0;
  const np = k.new_registered_workforce_payroll ?? 0;
  const tc = ec + nc;
  const tp = ep + np;

  return (
    <div data-testid="manpower-registration-details">
      <div className="border-2 border-slate-900 bg-white shadow-brutal-sm">
        <div className="border-b-2 border-slate-900 bg-ncc-navy px-5 py-4 text-center">
          <div className="industrial-label text-ncc-gold2">// Cross-Tab Matrix</div>
          <h3 className="mt-0.5 font-heading text-2xl font-black uppercase tracking-wider text-white">Manpower Registration Details</h3>
          <p className="mt-1 font-mono text-xs text-white/70">Workers in Nos · split by NCC engagement and employment type</p>
        </div>

        <table className="w-full border-collapse text-center text-sm">
          <thead>
            <tr>
              <th className="border-b-2 border-slate-900 bg-blueprint/20 px-4 py-3 font-heading text-base font-black text-ncc-navy" colSpan={2}>NCC-EXISTING</th>
              <th className="border-b-2 border-l border-slate-900 bg-blueprint/20 px-4 py-3 font-heading text-base font-black text-ncc-navy" colSpan={2}>NEW · NON-NCC</th>
              <th className="border-b-2 border-l border-slate-900 bg-ncc-gold/40 px-4 py-3 font-heading text-base font-black text-ncc-navy" colSpan={2}>TOTAL</th>
            </tr>
            <tr className="bg-slate-100">
              <th className="border-b border-slate-300 px-4 py-2 industrial-label text-slate-700">Contractor</th>
              <th className="border-b border-l border-slate-300 px-4 py-2 industrial-label text-slate-700">Payroll</th>
              <th className="border-b border-l-2 border-l-slate-900 border-slate-300 px-4 py-2 industrial-label text-slate-700">Contractor</th>
              <th className="border-b border-l border-slate-300 px-4 py-2 industrial-label text-slate-700">Payroll</th>
              <th className="border-b border-l-2 border-l-slate-900 border-slate-300 bg-ncc-gold/20 px-4 py-2 industrial-label text-ncc-navy">Contractor</th>
              <th className="border-b border-l border-slate-300 bg-ncc-gold/20 px-4 py-2 industrial-label text-ncc-navy">Payroll</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-6 font-heading text-3xl font-black text-slate-900" data-testid="mrd-existing-contractor">{ec.toLocaleString()}</td>
              <td className="border-l border-slate-200 px-4 py-6 font-heading text-3xl font-black text-slate-900" data-testid="mrd-existing-payroll">{ep.toLocaleString()}</td>
              <td className="border-l-2 border-l-slate-900 px-4 py-6 font-heading text-3xl font-black text-slate-900" data-testid="mrd-new-contractor">{nc.toLocaleString()}</td>
              <td className="border-l border-slate-200 px-4 py-6 font-heading text-3xl font-black text-slate-900" data-testid="mrd-new-payroll">{np.toLocaleString()}</td>
              <td className="border-l-2 border-l-slate-900 bg-ncc-gold/15 px-4 py-6 font-heading text-3xl font-black text-ncc-navy" data-testid="mrd-total-contractor">{tc.toLocaleString()}</td>
              <td className="border-l border-slate-200 bg-ncc-gold/15 px-4 py-6 font-heading text-3xl font-black text-ncc-navy" data-testid="mrd-total-payroll">{tp.toLocaleString()}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="bg-slate-900 text-white">
              <td className="px-4 py-3 industrial-label" colSpan={2}>Sub-Total: {(ec + ep).toLocaleString()}</td>
              <td className="border-l-2 border-l-ncc-gold px-4 py-3 industrial-label" colSpan={2}>Sub-Total: {(nc + np).toLocaleString()}</td>
              <td className="border-l-2 border-l-ncc-gold bg-ncc-gold/90 px-4 py-3 industrial-label text-ncc-navy" colSpan={2}>Grand Total: {(tc + tp).toLocaleString()}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="mt-3 font-mono text-[11px] text-slate-500">
        <b>NCC-Existing</b> = workers from approved records with status <b>NCCL</b>. <b>New · Non-NCC</b> = workers from other statuses. Workers split by employment type (Contractor / Payroll).
      </p>

      {/* Manpower Roster Details — safety induction roster counts (Contractor / Payroll / Total) */}
      <ManpowerRosterDetails k={k} />
    </div>
  );
}

// ---------- 5b. Manpower Roster Details (Roster status × Contractors / Payroll / Total) ----------
// Sibling of ManpowerRegistrationDetails — but shows the SAFETY INDUCTION roster
// numbers rather than registration counts. Rows: Approved / Pending / Rejected / TOTAL.
// Driven by the dashboard KPI keys: workmen_roster_{approved|pending|rejected}_{contractor|payroll}.
function ManpowerRosterDetails({ k }) {
  if (!k) return null;
  const rows = [
    {
      key: "approved",
      label: "Approved",
      cls: "text-emerald-700",
      contractor: k.workmen_roster_approved_contractor ?? 0,
      payroll: k.workmen_roster_approved_payroll ?? 0,
    },
    {
      key: "pending",
      label: "Pending",
      cls: "text-amber-700",
      contractor: k.workmen_roster_pending_contractor ?? 0,
      payroll: k.workmen_roster_pending_payroll ?? 0,
    },
    {
      key: "rejected",
      label: "Rejected",
      cls: "text-red-700",
      contractor: k.workmen_roster_rejected_contractor ?? 0,
      payroll: k.workmen_roster_rejected_payroll ?? 0,
    },
  ];
  const totals = rows.reduce(
    (a, r) => ({ contractor: a.contractor + r.contractor, payroll: a.payroll + r.payroll }),
    { contractor: 0, payroll: 0 },
  );

  return (
    <div className="mt-6 border-2 border-slate-900 bg-white shadow-brutal-sm" data-testid="manpower-roster-details">
      <div className="border-b-2 border-slate-900 bg-ncc-navy px-5 py-4 text-center">
        <div className="industrial-label text-ncc-gold2">// Safety Induction Roster</div>
        <h3 className="mt-0.5 font-heading text-2xl font-black uppercase tracking-wider text-white">Manpower Roster Details</h3>
        <p className="mt-1 font-mono text-xs text-white/70">Workmen + Payroll · split by safety induction status</p>
      </div>
      <table className="w-full border-collapse text-center text-sm">
        <thead>
          <tr className="bg-slate-100">
            <th className="border-b-2 border-slate-900 px-4 py-3 industrial-label text-slate-700 text-left">Roster Status</th>
            <th className="border-b-2 border-l border-slate-900 px-4 py-3 industrial-label text-slate-700">Contractors</th>
            <th className="border-b-2 border-l border-slate-900 px-4 py-3 industrial-label text-slate-700">Payroll</th>
            <th className="border-b-2 border-l border-slate-900 bg-ncc-gold/40 px-4 py-3 industrial-label text-ncc-navy">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const rowTotal = r.contractor + r.payroll;
            return (
              <tr key={r.key} className="border-t border-slate-200" data-testid={`mroster-row-${r.key}`}>
                <td className={`px-4 py-5 industrial-label text-left ${r.cls}`}>{r.label}</td>
                <td className="border-l border-slate-200 px-4 py-5 font-heading text-2xl font-black text-slate-900" data-testid={`mroster-${r.key}-contractor`}>{r.contractor.toLocaleString()}</td>
                <td className="border-l border-slate-200 px-4 py-5 font-heading text-2xl font-black text-slate-900" data-testid={`mroster-${r.key}-payroll`}>{r.payroll.toLocaleString()}</td>
                <td className="border-l border-slate-200 bg-ncc-gold/15 px-4 py-5 font-heading text-2xl font-black text-ncc-navy" data-testid={`mroster-${r.key}-total`}>{rowTotal.toLocaleString()}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-slate-900 text-white">
            <td className="px-4 py-3 industrial-label text-left">TOTAL</td>
            <td className="border-l-2 border-l-ncc-gold px-4 py-3 industrial-label" data-testid="mroster-total-contractor">{totals.contractor.toLocaleString()}</td>
            <td className="border-l-2 border-l-ncc-gold px-4 py-3 industrial-label" data-testid="mroster-total-payroll">{totals.payroll.toLocaleString()}</td>
            <td className="border-l-2 border-l-ncc-gold bg-ncc-gold/90 px-4 py-3 industrial-label text-ncc-navy" data-testid="mroster-grand-total">Grand Total: {(totals.contractor + totals.payroll).toLocaleString()}</td>
          </tr>
        </tfoot>
      </table>
      <p className="border-t-2 border-slate-900 bg-slate-50 px-5 py-2 font-mono text-[11px] text-slate-500">
        <b>Contractors</b> = workmen entries (db.workmen) under Labour Contractors. <b>Payroll</b> = Payroll Manpower records with corresponding <b>pay_roster_status</b>. Mirrors the dashboard Workmen Roster KPI.
      </p>
    </div>
  );
}

// ---------- 6. Contractor NCC Workforce Details ----------
function ContractorNCCWorkforce() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sites, setSites] = useState([]);
  const [editingId, setEditingId] = useState(null);
  // draft.allocations: { siteName: numberString }
  const [draft, setDraft] = useState({ allocations: {} });
  const [saving, setSaving] = useState(false);
  const [pickerSel, setPickerSel] = useState("");
  // Filters
  const [filterType, setFilterType] = useState("");   // Contractor Type
  const [filterSite, setFilterSite] = useState("");   // NCC Site

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/contractors?include_pending=true"),
      api.get("/meta/trades").catch(() => ({ data: { ncc_sites: [] } })),
    ])
      .then(([cr, mr]) => {
        setList((cr.data || []).filter((c) => !c.is_payroll && c.status === "approved"));
        setSites(mr.data?.ncc_sites || []);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  // Currently-working-in-NCC = current_status==NCCL (deployed) OR has at least one site assigned
  function isInNcc(c) {
    if ((c.current_status || "").toUpperCase() === "NCCL") return true;
    if (Array.isArray(c.ncc_site_names) && c.ncc_site_names.length > 0) return true;
    return !!(c.ncc_site_name && c.ncc_site_name.trim());
  }

  function sitesOf(c) {
    if (Array.isArray(c.ncc_site_names) && c.ncc_site_names.length) return c.ncc_site_names;
    if (c.ncc_site_name) return c.ncc_site_name.split(",").map((s) => s.trim()).filter(Boolean);
    return [];
  }

  // Returns { site: nos } map for a contractor. Falls back to even-ish distribution when allocations missing.
  function allocOf(c) {
    const alloc = c.ncc_site_allocations || {};
    const ss = sitesOf(c);
    if (ss.length === 0) return {};
    // If backend already has allocations covering all sites, use it
    const out = {};
    let coveredCount = 0;
    ss.forEach((s) => {
      if (alloc[s] != null) { out[s] = Number(alloc[s]) || 0; coveredCount++; }
      else out[s] = 0;
    });
    if (coveredCount === 0 && (c.current_status || "").toUpperCase() === "NCCL") {
      // Legacy record: put the full ready_nos onto first site so the totals match
      const totalNos = Number(c.ready_nos || c.crew_size || 0);
      out[ss[0]] = totalNos;
    }
    return out;
  }

  function nccNosOf(c) {
    const a = allocOf(c);
    const sum = Object.values(a).reduce((acc, v) => acc + (Number(v) || 0), 0);
    if (sum > 0) return sum;
    // legacy fallback
    return (c.current_status || "").toUpperCase() === "NCCL" ? Number(c.ready_nos || c.crew_size || 0) : 0;
  }

  // Distinct contractor types from records (for the filter dropdown)
  const contractorTypes = useMemo(() => {
    const s = new Set();
    list.forEach((c) => { if (c.contractor_type) s.add(c.contractor_type); });
    return Array.from(s).sort();
  }, [list]);

  const filtered = useMemo(() => {
    let arr = list.filter(isInNcc);
    if (filterType) arr = arr.filter((c) => (c.contractor_type || "") === filterType);
    if (filterSite) arr = arr.filter((c) => sitesOf(c).includes(filterSite));
    const s = q.trim().toLowerCase();
    if (s) arr = arr.filter((c) => [c.name, c.id_proof_number, c.contractor_type, ...sitesOf(c)]
      .filter(Boolean).join(" ").toLowerCase().includes(s));
    return arr;
  }, [list, q, filterType, filterSite]);

  function startEdit(c) {
    setEditingId(c.id);
    const a = allocOf(c);
    // Stringify values for input controls
    const allocStr = {};
    Object.entries(a).forEach(([k, v]) => { allocStr[k] = String(v || 0); });
    setDraft({ allocations: allocStr });
    setPickerSel("");
  }
  function cancelEdit() { setEditingId(null); setDraft({ allocations: {} }); setPickerSel(""); }

  function addSite(name) {
    const n = (name || "").trim();
    if (!n) return;
    setDraft((p) => p.allocations[n] != null ? p : { ...p, allocations: { ...p.allocations, [n]: "0" } });
    setPickerSel("");
  }
  function removeSite(name) {
    setDraft((p) => {
      const next = { ...p.allocations };
      delete next[name];
      return { ...p, allocations: next };
    });
  }
  function setSiteNos(name, value) {
    setDraft((p) => ({ ...p, allocations: { ...p.allocations, [name]: value } }));
  }

  function draftTotal() {
    return Object.values(draft.allocations || {}).reduce((acc, v) => acc + (Number(v) || 0), 0);
  }

  async function saveEdit(c) {
    const allocNum = {};
    let total = 0;
    for (const [k, v] of Object.entries(draft.allocations || {})) {
      const n = Number(v || 0);
      if (Number.isNaN(n) || n < 0) { toast.error(`NCC Nos for '${k}' must be ≥ 0`); return; }
      allocNum[k] = n;
      total += n;
    }
    if (Number(c.crew_size || 0) > 0 && total > Number(c.crew_size)) {
      toast.error(`Total NCC Nos (${total}) cannot exceed Total Workforce Capacity (${c.crew_size})`); return;
    }
    setSaving(true);
    try {
      const body = { ncc_site_allocations: allocNum };
      if (Object.keys(allocNum).length === 0) body.current_status = "NEW";
      const r = await api.patch(`/contractors/${c.id}/ncc-deployment`, body);
      setList((prev) => prev.map((x) => x.id === c.id ? { ...x, ...r.data } : x));
      toast.success("NCC deployment updated");
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  const totals = useMemo(() => filtered.reduce((a, c) => {
    const total = Number(c.crew_size || 0);
    const nccNos = nccNosOf(c);
    a.cap += total; a.ncc += nccNos; a.reserve += Math.max(total - nccNos, 0);
    return a;
  }, { cap: 0, ncc: 0, reserve: 0 }), [filtered]);
  const grandPct = totals.cap > 0 ? Math.round((totals.ncc / totals.cap) * 1000) / 10 : 0;

  return (
    <div data-testid="contractor-ncc-report">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-900 bg-ncc-navy px-5 py-4">
        <div>
          <div className="industrial-label text-ncc-gold2">// Detail</div>
          <h3 className="mt-0.5 font-heading text-xl font-black text-white">Contractors Currently Working in NCC · Workforce Details</h3>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, PAN, site…"
          data-testid="cnw-search"
          className="w-64 border-2 border-ncc-gold bg-white px-3 py-2 font-mono text-sm focus:outline-none"
        />
      </div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 border-x-2 border-slate-900 bg-slate-50 px-5 py-3">
        <span className="industrial-label text-ncc-navy">// Filters</span>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          data-testid="cnw-filter-type"
          className="border-2 border-ncc-navy bg-white px-3 py-1.5 font-mono text-xs focus:outline-none"
        >
          <option value="">All Contractor Types</option>
          {contractorTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={filterSite}
          onChange={(e) => setFilterSite(e.target.value)}
          data-testid="cnw-filter-site"
          className="border-2 border-ncc-navy bg-white px-3 py-1.5 font-mono text-xs focus:outline-none"
        >
          <option value="">All NCC Sites</option>
          {sites.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(filterType || filterSite || q) && (
          <button
            type="button"
            onClick={() => { setFilterType(""); setFilterSite(""); setQ(""); }}
            data-testid="cnw-filter-clear"
            className="border-2 border-slate-400 bg-white px-3 py-1.5 industrial-label text-[10px] text-slate-700 hover:bg-slate-100"
          >Clear</button>
        )}
        <span className="ml-auto font-mono text-[11px] text-slate-600">
          Showing <b>{filtered.length}</b> of <b>{list.filter(isInNcc).length}</b> contractor(s)
        </span>
      </div>
      <div className="border-x-2 border-b-2 border-slate-900 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="px-3 py-2 text-left industrial-label">Name of Contractor</th>
                <th className="px-3 py-2 text-left industrial-label">PAN No</th>
                <th className="px-3 py-2 text-left industrial-label">Contractor Type</th>
                <th className="px-3 py-2 text-right industrial-label">Total Workforce<br/>Capacity</th>
                <th className="px-3 py-2 text-left industrial-label">NCC Site(s) · Nos</th>
                <th className="px-3 py-2 text-right industrial-label">Total<br/>NCC Nos</th>
                <th className="px-3 py-2 text-right industrial-label">Mobilization<br/>Reserve</th>
                <th className="px-3 py-2 text-right industrial-label">Deployment %</th>
                <th className="px-3 py-2 text-left industrial-label">Remarks</th>
                <th className="px-3 py-2 text-center industrial-label">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} className="p-6 text-center font-mono text-slate-500">No contractor records.</td></tr>
              )}
              {filtered.map((c) => {
                const total = Number(c.crew_size || 0);
                const alloc = allocOf(c);
                const isEditing = editingId === c.id;
                const nccNos = isEditing ? draftTotal() : nccNosOf(c);
                const reserve = Math.max(total - nccNos, 0);
                const pct = total > 0 ? Math.round((nccNos / total) * 1000) / 10 : 0;
                const pan = c.id_proof_type === "PAN" ? (c.id_proof_number || "") : "";
                const availableSites = sites.filter((s) => draft.allocations[s] == null);
                return (
                  <tr key={c.id} className={`border-t border-slate-200 ${isEditing ? "bg-ncc-tint" : "hover:bg-slate-50"}`} data-testid={`cnw-row-${c.id}`}>
                    <td className="px-3 py-2 font-bold text-slate-900">{c.name}</td>
                    <td className="px-3 py-2 font-mono">{pan || "—"}</td>
                    <td className="px-3 py-2">{c.contractor_type || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{total.toLocaleString()}</td>
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <div className="flex flex-col gap-1.5" data-testid={`cnw-sites-editor-${c.id}`}>
                          {Object.keys(draft.allocations).length === 0 && (
                            <span className="font-mono text-[11px] italic text-slate-500">No sites selected</span>
                          )}
                          {Object.entries(draft.allocations).map(([s, v]) => (
                            <div key={s} className="flex items-center gap-1.5" data-testid={`cnw-alloc-row-${c.id}-${s}`}>
                              <span className="flex-1 inline-flex items-center gap-1 rounded border-2 border-ncc-navy bg-white px-1.5 py-0.5 font-mono text-[10px] font-bold text-ncc-navy">
                                {s}
                                <button
                                  type="button"
                                  onClick={() => removeSite(s)}
                                  className="ml-auto text-red-600 hover:text-red-800"
                                  aria-label={`Remove ${s}`}
                                  data-testid={`cnw-site-remove-${c.id}-${s}`}
                                >×</button>
                              </span>
                              <input
                                type="number"
                                min="0"
                                value={v}
                                onChange={(e) => setSiteNos(s, e.target.value)}
                                data-testid={`cnw-alloc-input-${c.id}-${s}`}
                                placeholder="Nos"
                                className="w-16 border-2 border-ncc-navy bg-white px-1.5 py-0.5 text-right font-mono text-xs focus:outline-none"
                              />
                            </div>
                          ))}
                          <div className="flex gap-1">
                            <select
                              value={pickerSel}
                              onChange={(e) => setPickerSel(e.target.value)}
                              data-testid={`cnw-site-select-${c.id}`}
                              className="flex-1 border-2 border-ncc-navy bg-white px-2 py-1 font-mono text-xs focus:outline-none"
                            >
                              <option value="">Select NCC Site (master)…</option>
                              {availableSites.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => addSite(pickerSel)}
                              disabled={!pickerSel}
                              data-testid={`cnw-site-add-${c.id}`}
                              className="rounded border-2 border-ncc-navy bg-ncc-gold px-2 py-1 industrial-label text-[10px] text-ncc-navy disabled:opacity-40"
                            >Add</button>
                          </div>
                          {availableSites.length === 0 && Object.keys(draft.allocations).length > 0 && (
                            <span className="font-mono text-[10px] text-slate-500">All master sites added.</span>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {sitesOf(c).length > 0
                            ? sitesOf(c).map((s) => {
                                const n = Number(alloc[s] || 0);
                                return (
                                  <span key={s} className="inline-flex items-center gap-1 rounded bg-ncc-tint px-1.5 py-0.5 font-mono text-[10px] font-bold text-ncc-navy" data-testid={`cnw-site-badge-${c.id}-${s}`}>
                                    {s}
                                    <span className="rounded bg-ncc-navy px-1 text-white">{n}</span>
                                  </span>
                                );
                              })
                            : <span className="text-slate-400">—</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {isEditing ? (
                        <span className="font-black text-emerald-700" data-testid={`cnw-draft-total-${c.id}`}>{draftTotal().toLocaleString()}</span>
                      ) : (<span className="text-emerald-700">{nccNos.toLocaleString()}</span>)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-blueprint">{reserve.toLocaleString()}</td>
                    <td className={`px-3 py-2 text-right font-mono font-black ${pct >= 80 ? "text-emerald-700" : pct >= 40 ? "text-amber-700" : "text-red-700"}`}>{pct}%</td>
                    <td className="px-3 py-2 max-w-xs truncate text-slate-600">{c.bio || ""}</td>
                    <td className="px-3 py-2 text-center">
                      {isEditing ? (
                        <div className="inline-flex gap-1">
                          <button onClick={() => saveEdit(c)} disabled={saving} data-testid={`cnw-save-${c.id}`}
                            className="border border-ncc-navy bg-emerald-500 px-1.5 py-1 text-white hover:bg-emerald-600 disabled:opacity-60">
                            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          </button>
                          <button onClick={cancelEdit} disabled={saving} data-testid={`cnw-cancel-${c.id}`}
                            className="border border-slate-300 bg-white px-1.5 py-1 text-slate-700 hover:bg-slate-100">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => startEdit(c)} data-testid={`cnw-edit-${c.id}`}
                          title="Edit Site & NCC Nos"
                          className="border border-slate-300 bg-white p-1.5 text-ncc-navy hover:border-ncc-navy hover:bg-ncc-tint">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-ncc-gold/30">
                <tr className="font-bold">
                  <td colSpan={3} className="px-3 py-3 industrial-label text-ncc-navy">GRAND TOTAL · {filtered.length} record(s)</td>
                  <td className="px-3 py-3 text-right font-heading text-base font-black text-ncc-navy">{totals.cap.toLocaleString()}</td>
                  <td></td>
                  <td className="px-3 py-3 text-right font-heading text-base font-black text-emerald-800">{totals.ncc.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right font-heading text-base font-black text-blueprint">{totals.reserve.toLocaleString()}</td>
                  <td className="px-3 py-3 text-right font-heading text-base font-black text-ncc-navy">{grandPct}%</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      <p className="mt-3 font-mono text-[11px] text-slate-500">
        Click the <Pencil className="inline h-3 w-3" /> pencil to inline-edit <b>NCC Site(s) + per-site NCC Nos</b>. Sites are picked from the <b>NCC Sites master</b>. Saving auto-sets status to <b>NCCL</b>. Clearing all sites reverts to <b>NEW</b>. Total NCC Nos = sum of site values · Mobilization Reserve = Capacity − Total NCC Nos · Deployment % = Total NCC Nos ÷ Capacity.
      </p>
    </div>
  );
}


// ---------- 7. Payroll NCC Workforce Details ----------
function PayrollNCCWorkforce() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [onlyNccl, setOnlyNccl] = useState(true);
  const [sites, setSites] = useState([]);
  const [trades, setTrades] = useState([]);
  // Filters
  const [filterType, setFilterType] = useState("");   // Contractor Type
  const [filterSite, setFilterSite] = useState("");   // Site Name (master)
  const [filterTrade, setFilterTrade] = useState(""); // Trade (master)

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/contractors?include_pending=true"),
      api.get("/meta/trades").catch(() => ({ data: { ncc_sites: [], trades: [] } })),
    ])
      .then(([cr, mr]) => {
        setList((cr.data || []).filter((c) => c.is_payroll && c.status === "approved"));
        setSites(mr.data?.ncc_sites || []);
        setTrades(mr.data?.trades || []);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  // Distinct contractor types across payroll records (typically NCCL on-roll / AGENCY)
  const contractorTypes = useMemo(() => {
    const s = new Set();
    list.forEach((c) => {
      const t = c.contractor_type || c.source;
      if (t) s.add(t);
    });
    return Array.from(s).sort();
  }, [list]);

  // Trades — union of master trades + any trades present in records (to avoid hiding values)
  const tradeOptions = useMemo(() => {
    const s = new Set(trades);
    list.forEach((c) => {
      if (c.trade) s.add(c.trade);
      (c.trades || []).forEach((t) => t && s.add(t));
    });
    return Array.from(s).sort();
  }, [trades, list]);

  function recordHasTrade(c, t) {
    if (!t) return true;
    if (c.trade === t) return true;
    return Array.isArray(c.trades) && c.trades.includes(t);
  }

  const filtered = useMemo(() => {
    let arr = list;
    if (onlyNccl) arr = arr.filter((c) => (c.current_status || "").toUpperCase() === "NCCL");
    if (filterType) arr = arr.filter((c) => (c.contractor_type || c.source || "") === filterType);
    if (filterSite) arr = arr.filter((c) => (c.ncc_site_name || "") === filterSite);
    if (filterTrade) arr = arr.filter((c) => recordHasTrade(c, filterTrade));
    const s = q.trim().toLowerCase();
    if (s) arr = arr.filter((c) => [c.name, c.contact_phone, c.trade, c.ncc_site_name, ...(c.trades || [])]
      .filter(Boolean).join(" ").toLowerCase().includes(s));
    return arr;
  }, [list, q, onlyNccl, filterType, filterSite, filterTrade]);

  const totalEligible = useMemo(() => {
    return list.filter((c) => !onlyNccl || (c.current_status || "").toUpperCase() === "NCCL").length;
  }, [list, onlyNccl]);

  return (
    <div data-testid="payroll-ncc-report">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-900 bg-ncc-navy px-5 py-4">
        <div>
          <div className="industrial-label text-ncc-gold2">// Detail</div>
          <h3 className="mt-0.5 font-heading text-xl font-black text-white">Payroll Manpower · NCC Workforce Details</h3>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 font-mono text-xs text-white">
            <input type="checkbox" checked={onlyNccl} onChange={(e) => setOnlyNccl(e.target.checked)} data-testid="pnw-only-nccl" />
            Show NCCL only
          </label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, mobile, trade…"
            data-testid="pnw-search"
            className="w-64 border-2 border-ncc-gold bg-white px-3 py-2 font-mono text-sm focus:outline-none"
          />
        </div>
      </div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 border-x-2 border-slate-900 bg-slate-50 px-5 py-3">
        <span className="industrial-label text-ncc-navy">// Filters</span>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          data-testid="pnw-filter-type"
          className="border-2 border-ncc-navy bg-white px-3 py-1.5 font-mono text-xs focus:outline-none"
        >
          <option value="">All Contractor Types</option>
          {contractorTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={filterSite}
          onChange={(e) => setFilterSite(e.target.value)}
          data-testid="pnw-filter-site"
          className="border-2 border-ncc-navy bg-white px-3 py-1.5 font-mono text-xs focus:outline-none"
        >
          <option value="">All NCC Sites</option>
          {sites.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filterTrade}
          onChange={(e) => setFilterTrade(e.target.value)}
          data-testid="pnw-filter-trade"
          className="border-2 border-ncc-navy bg-white px-3 py-1.5 font-mono text-xs focus:outline-none"
        >
          <option value="">All Trades</option>
          {tradeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {(filterType || filterSite || filterTrade || q) && (
          <button
            type="button"
            onClick={() => { setFilterType(""); setFilterSite(""); setFilterTrade(""); setQ(""); }}
            data-testid="pnw-filter-clear"
            className="border-2 border-slate-400 bg-white px-3 py-1.5 industrial-label text-[10px] text-slate-700 hover:bg-slate-100"
          >Clear</button>
        )}
        <span className="ml-auto font-mono text-[11px] text-slate-600">
          Showing <b>{filtered.length}</b> of <b>{totalEligible}</b> employee(s)
        </span>
      </div>
      <div className="border-x-2 border-b-2 border-slate-900 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left industrial-label">Name of Employee</th>
              <th className="px-3 py-2 text-left industrial-label">Mobile No.</th>
              <th className="px-3 py-2 text-left industrial-label">Trade</th>
              <th className="px-3 py-2 text-left industrial-label">Site Name</th>
              <th className="px-3 py-2 text-left industrial-label">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center font-mono text-slate-500">No payroll records{onlyNccl ? " currently at NCC" : ""}.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`pnw-row-${c.id}`}>
                <td className="px-3 py-2 font-bold text-slate-900">{c.name}</td>
                <td className="px-3 py-2 font-mono">{c.contact_phone || "—"}</td>
                <td className="px-3 py-2">{c.trade || (c.trades || []).join(", ") || "—"}</td>
                <td className="px-3 py-2">{c.ncc_site_name || "—"}</td>
                <td className="px-3 py-2 max-w-md truncate text-slate-600">{c.bio || ""}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="bg-ncc-gold/30">
              <tr className="font-bold"><td colSpan={5} className="px-3 py-3 industrial-label text-ncc-navy">TOTAL · {filtered.length} employee(s)</td></tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ---------- 8. New Contractors (Non-NCC) ----------
function NewContractorsReport() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/contractors?include_pending=true")
      .then((r) => setList((r.data || []).filter((c) => !c.is_payroll && c.status === "approved" && (c.current_status || "").toUpperCase() !== "NCCL")))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((c) => [c.name, c.id_proof_number, c.contractor_type, c.contact_phone, c.contact_email]
      .filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [list, q]);

  // Build union of all trades across the filtered list — these become dynamic sub-columns under "TRADE"
  const tradeCols = useMemo(() => {
    const set = new Set();
    filtered.forEach((c) => {
      Object.keys(c.trade_counts || {}).forEach((t) => t && set.add(t));
      (c.trades || []).forEach((t) => t && set.add(t));
    });
    return Array.from(set).sort();
  }, [filtered]);

  const totals = useMemo(() => {
    const t = { cap: 0, ready: 0, per: {} };
    filtered.forEach((c) => {
      t.cap += Number(c.crew_size || 0);
      t.ready += Number(c.ready_nos || 0);
      tradeCols.forEach((tr) => {
        t.per[tr] = (t.per[tr] || 0) + Number((c.trade_counts || {})[tr] || 0);
      });
    });
    return t;
  }, [filtered, tradeCols]);

  return (
    <div data-testid="new-contractors-report">
      <div className="flex items-center justify-between gap-3 border-b-2 border-slate-900 bg-ncc-navy px-5 py-4">
        <div>
          <div className="industrial-label text-ncc-gold2">// Non-NCC</div>
          <h3 className="mt-0.5 font-heading text-xl font-black text-white">New Contractors (Non-NCC)</h3>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, PAN, phone…"
          data-testid="newc-search"
          className="w-64 border-2 border-ncc-gold bg-white px-3 py-2 font-mono text-sm focus:outline-none"
        />
      </div>
      <div className="border-x-2 border-b-2 border-slate-900 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th rowSpan={2} className="border-b border-slate-300 px-3 py-2 text-left industrial-label">Name of Contractor</th>
                <th rowSpan={2} className="border-b border-slate-300 px-3 py-2 text-left industrial-label">Contractor Type</th>
                <th rowSpan={2} className="border-b border-slate-300 px-3 py-2 text-left industrial-label">PAN No</th>
                <th rowSpan={2} className="border-b border-slate-300 px-3 py-2 text-left industrial-label">Phone</th>
                <th rowSpan={2} className="border-b border-slate-300 px-3 py-2 text-left industrial-label">Email</th>
                <th rowSpan={2} className="border-b border-slate-300 px-3 py-2 text-right industrial-label">Total<br/>Workforce<br/>Capacity</th>
                {tradeCols.length > 0 && (
                  <th colSpan={tradeCols.length} className="border-b border-l-2 border-l-slate-900 border-slate-300 bg-blueprint/10 px-3 py-2 text-center industrial-label">Trade <span className="text-[10px] text-slate-500">(As per Registration)</span></th>
                )}
                <th rowSpan={2} className="border-b border-l-2 border-l-slate-900 border-slate-300 px-3 py-2 text-right industrial-label">Ready to<br/>Mobilize<br/><span className="text-[10px] text-slate-500">(As per Ready Signal)</span></th>
                <th rowSpan={2} className="border-b border-slate-300 px-3 py-2 text-left industrial-label">Remarks</th>
              </tr>
              {tradeCols.length > 0 && (
                <tr>
                  {tradeCols.map((tr, idx) => (
                    <th key={tr} className={`border-slate-300 bg-blueprint/10 px-2 py-1.5 text-center industrial-label ${idx === 0 ? "border-l-2 border-l-slate-900" : ""}`}>
                      <span className="text-[10px] uppercase">{tr}</span>
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7 + tradeCols.length} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7 + tradeCols.length} className="p-6 text-center font-mono text-slate-500">No new (non-NCC) contractor records.</td></tr>
              )}
              {filtered.map((c) => {
                const pan = c.id_proof_type === "PAN" ? (c.id_proof_number || "") : "";
                const tc = c.trade_counts || {};
                return (
                  <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`newc-row-${c.id}`}>
                    <td className="px-3 py-2 font-bold text-slate-900">{c.name}</td>
                    <td className="px-3 py-2">{c.contractor_type || "—"}</td>
                    <td className="px-3 py-2 font-mono">{pan || "—"}</td>
                    <td className="px-3 py-2 font-mono">{c.contact_phone || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">{c.contact_email || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(c.crew_size || 0).toLocaleString()}</td>
                    {tradeCols.map((tr, idx) => (
                      <td key={tr} className={`px-2 py-2 text-center font-mono ${idx === 0 ? "border-l-2 border-l-slate-900" : "border-l border-slate-200"}`}>
                        {tc[tr] ? <span className="font-bold text-ncc-navy">{tc[tr]}</span> : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                    <td className="border-l-2 border-l-slate-900 px-3 py-2 text-right font-mono">
                      {c.ready_nos ? (
                        <div>
                          <div className="font-bold text-emerald-700">{c.ready_nos}</div>
                          {c.ready_date && <div className="text-[10px] text-slate-500">{c.ready_date}</div>}
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 max-w-xs truncate text-slate-600">{c.bio || ""}</td>
                  </tr>
                );
              })}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-ncc-gold/30">
                <tr className="font-bold">
                  <td colSpan={5} className="px-3 py-3 industrial-label text-ncc-navy">TOTAL · {filtered.length} record(s)</td>
                  <td className="px-3 py-3 text-right font-heading text-base font-black text-ncc-navy">{totals.cap.toLocaleString()}</td>
                  {tradeCols.map((tr, idx) => (
                    <td key={tr} className={`px-2 py-3 text-center font-mono font-black text-ncc-navy ${idx === 0 ? "border-l-2 border-l-slate-900" : "border-l border-slate-200"}`}>{totals.per[tr] || 0}</td>
                  ))}
                  <td className="border-l-2 border-l-slate-900 px-3 py-3 text-right font-heading text-base font-black text-emerald-800">{totals.ready.toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      <p className="mt-3 font-mono text-[11px] text-slate-500">
        Contractor records with status NOT in NCCL. Trade columns auto-generated from the union of trades across these contractors.
      </p>
    </div>
  );
}

// ---------- 9. New Payroll Manpower (Non-NCC) ----------
function NewPayrollReport() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/contractors?include_pending=true")
      .then((r) => setList((r.data || []).filter((c) => c.is_payroll && c.status === "approved" && (c.current_status || "").toUpperCase() !== "NCCL")))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((c) => [c.name, c.contact_phone, c.trade, c.state, c.city]
      .filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [list, q]);

  function expYears(c) {
    const total = Number(c.experience_india_years || 0) + Number(c.experience_abroad_years || 0);
    return total > 0 ? total : (c.experience_years || 0);
  }

  return (
    <div data-testid="new-payroll-report">
      <div className="flex items-center justify-between gap-3 border-b-2 border-slate-900 bg-ncc-navy px-5 py-4">
        <div>
          <div className="industrial-label text-ncc-gold2">// Non-NCC</div>
          <h3 className="mt-0.5 font-heading text-xl font-black text-white">New Payroll Manpower (Non-NCC)</h3>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, mobile, trade…"
          data-testid="newp-search"
          className="w-64 border-2 border-ncc-gold bg-white px-3 py-2 font-mono text-sm focus:outline-none"
        />
      </div>
      <div className="border-x-2 border-b-2 border-slate-900 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left industrial-label">Employee Name</th>
              <th className="px-3 py-2 text-left industrial-label">Mobile</th>
              <th className="px-3 py-2 text-left industrial-label">Trade</th>
              <th className="px-3 py-2 text-right industrial-label">Experience (yrs)</th>
              <th className="px-3 py-2 text-left industrial-label">State</th>
              <th className="px-3 py-2 text-left industrial-label">City</th>
              <th className="px-3 py-2 text-right industrial-label">Ready to Join</th>
              <th className="px-3 py-2 text-left industrial-label">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center font-mono text-slate-500">No new (non-NCC) payroll records.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`newp-row-${c.id}`}>
                <td className="px-3 py-2 font-bold text-slate-900">{c.name}</td>
                <td className="px-3 py-2 font-mono">{c.contact_phone || "—"}</td>
                <td className="px-3 py-2">{c.trade || (c.trades || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{expYears(c)}</td>
                <td className="px-3 py-2">{c.state || "—"}</td>
                <td className="px-3 py-2">{c.city || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {c.ready_date ? (
                    <div className="text-emerald-700">
                      <div className="text-[10px]">From</div>
                      <div className="font-bold">{c.ready_date}</div>
                    </div>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 max-w-md truncate text-slate-600">{c.bio || ""}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="bg-ncc-gold/30">
              <tr className="font-bold"><td colSpan={8} className="px-3 py-3 industrial-label text-ncc-navy">TOTAL · {filtered.length} employee(s)</td></tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="mt-3 font-mono text-[11px] text-slate-500">
        Payroll records with status NOT in NCCL. Ready to Join uses the date set during Ready Signal.
      </p>
    </div>
  );
}

// ---------- 10. Workmen-Pay Roster (toggle: Workmen / Payroll → 4 reports each) ----------
// Maps an approved Payroll Manpower contractor record into a Workman-shaped row
// so the existing 4 sub-reports (Summary · Medical · Induction · Detailed) render
// identically for Payroll Roster data. Keeps the reports DRY.
function _payrollContractorToWorkman(c) {
  const idDigits = c.id_proof_type === "Aadhaar" ? (c.id_proof_number || "").replace(/\D/g, "") : "";
  return {
    id: c.id,
    contractor_id: c.id,
    name: c.name || "",
    father_husband_name: c.father_husband_name || "",
    trade: (c.trades && c.trades[0]) || "",
    mobile: c.contact_phone || "",
    ncc_site_name: c.ncc_site_name || "",
    gender: c.gender || "",
    blood_group: c.blood_group || "",
    date_of_birth: c.date_of_birth || "",
    age_years: c.age_years ?? null,
    id_proof_type: c.id_proof_type || "",
    id_proof_number: c.id_proof_number || "",
    aadhaar: idDigits,
    aadhaar_normalized: idDigits,
    pincode: c.pincode || "",
    village: c.village || "",
    city: c.city || "",
    state: c.state || "",
    address: c.address || "",
    nationality: c.nationality || "Indian",
    religion: c.religion || "",
    category: c.category || "",
    experience_years: c.experience_years || 0,
    marital_status: c.marital_status || "",
    children_count: c.children_count ?? null,
    mother_tongue: c.mother_tongue || "",
    other_languages: c.other_languages || "",
    emergency_contact_name: c.emergency_contact_name || "",
    emergency_contact_number: c.emergency_contact_number || "",
    vision: c.vision || "",
    height: c.height || "",
    weight: c.weight || "",
    hearing: c.hearing || "",
    physical_deformities: c.physical_deformities || "",
    medical_history: c.medical_history || "",
    phys_exam_blood_pressure: c.phys_exam_blood_pressure || "",
    phys_exam_pulse: c.phys_exam_pulse || "",
    phys_exam_refractive_error: c.phys_exam_refractive_error || "",
    phys_exam_colour_vision: c.phys_exam_colour_vision || "",
    phys_exam_disability: c.phys_exam_disability || "",
    phys_exam_arm_function: c.phys_exam_arm_function || "",
    phys_exam_leg_function: c.phys_exam_leg_function || "",
    hist_varicose: c.hist_varicose || "",
    hist_seizure: c.hist_seizure || "",
    hist_vertigo: c.hist_vertigo || "",
    hist_acrophobia: c.hist_acrophobia || "",
    hist_diabetes: c.hist_diabetes || "",
    hist_stroke: c.hist_stroke || "",
    hist_heart_diseases: c.hist_heart_diseases || "",
    hist_major_illness_surgery: c.hist_major_illness_surgery || "",
    hist_symptoms_visible: c.hist_symptoms_visible || "",
    hist_others: c.hist_others || "",
    photo_base64: c.photo_base64 || (c.photo?.url || ""),
    status: c.pay_roster_status === "approved" ? "approved" : (c.pay_roster_status || "pending"),
    is_active: c.is_active !== false,
    created_at: c.pay_roster_submitted_at || c.created_at || "",
  };
}

// Date filter primitives shared across the Workmen-Pay Roster tab. Modes:
//   today   — today's date (default)
//   weekly  — last 7 days ending today
//   monthly — first→last day of current month
//   asOn    — single date (filter == single day)
//   range   — explicit from/to
// Returns {from, to} as ISO date strings (YYYY-MM-DD) or null when no filter.
function resolveDateRange(filter) {
  if (!filter || !filter.mode || filter.mode === "all") return { from: null, to: null };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  if (filter.mode === "today") {
    return { from: iso(today), to: iso(today) };
  }
  if (filter.mode === "weekly") {
    const from = new Date(today); from.setDate(from.getDate() - 6);
    return { from: iso(from), to: iso(today) };
  }
  if (filter.mode === "monthly") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: iso(from), to: iso(to) };
  }
  if (filter.mode === "asOn") {
    return { from: filter.asOn || null, to: filter.asOn || null };
  }
  if (filter.mode === "range") {
    return { from: filter.from || null, to: filter.to || null };
  }
  return { from: null, to: null };
}

// Apply a {from, to} date range to a list, picking the row's date via accessor.
function applyDateFilter(rows, range, accessor) {
  const { from, to } = range || {};
  if (!from && !to) return rows;
  return rows.filter((r) => {
    const raw = accessor(r);
    if (!raw) return false;
    const d = String(raw).slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });
}

function _DateFilterBtn({ id, label, active, onClick, testidPrefix }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`${testidPrefix}-${id}`}
      className={`inline-flex items-center gap-1 border-l-2 border-ncc-navy px-3 py-1.5 industrial-label text-[11px] transition-colors ${
        active ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );
}

function DateFilterBar({ value, onChange, testidPrefix = "date-filter" }) {
  const m = value?.mode || "today";
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="flex flex-wrap items-center gap-3 border-l-4 border-blueprint bg-slate-50 px-3 py-2" data-testid={`${testidPrefix}-bar`}>
      <span className="industrial-label text-[10px] text-slate-500">// Date Filter</span>
      <div className="inline-flex overflow-hidden rounded-md border-2 border-ncc-navy">
        <_DateFilterBtn id="all" label="All" active={m === "all"} onClick={() => set({ mode: "all" })} testidPrefix={testidPrefix} />
        <_DateFilterBtn id="today" label="Daily" active={m === "today"} onClick={() => set({ mode: "today" })} testidPrefix={testidPrefix} />
        <_DateFilterBtn id="weekly" label="Weekly" active={m === "weekly"} onClick={() => set({ mode: "weekly" })} testidPrefix={testidPrefix} />
        <_DateFilterBtn id="monthly" label="Monthly" active={m === "monthly"} onClick={() => set({ mode: "monthly" })} testidPrefix={testidPrefix} />
        <_DateFilterBtn id="asOn" label="As On" active={m === "asOn"} onClick={() => set({ mode: "asOn" })} testidPrefix={testidPrefix} />
        <_DateFilterBtn id="range" label="Date Range" active={m === "range"} onClick={() => set({ mode: "range" })} testidPrefix={testidPrefix} />
      </div>
      {m === "asOn" && (
        <input
          type="date"
          value={value?.asOn || ""}
          onChange={(e) => set({ asOn: e.target.value })}
          data-testid={`${testidPrefix}-asOn-input`}
          className="border-2 border-slate-300 bg-white px-2 py-1 font-mono text-[12px] focus:border-ncc-navy focus:outline-none"
        />
      )}
      {m === "range" && (
        <div className="flex items-center gap-1">
          <span className="industrial-label text-[10px] text-slate-500">From</span>
          <input
            type="date"
            value={value?.from || ""}
            onChange={(e) => set({ from: e.target.value })}
            data-testid={`${testidPrefix}-from-input`}
            className="border-2 border-slate-300 bg-white px-2 py-1 font-mono text-[12px] focus:border-ncc-navy focus:outline-none"
          />
          <span className="industrial-label text-[10px] text-slate-500">To</span>
          <input
            type="date"
            value={value?.to || ""}
            onChange={(e) => set({ to: e.target.value })}
            data-testid={`${testidPrefix}-to-input`}
            className="border-2 border-slate-300 bg-white px-2 py-1 font-mono text-[12px] focus:border-ncc-navy focus:outline-none"
          />
        </div>
      )}
      <span className="ml-auto font-mono text-[11px] text-slate-500" data-testid={`${testidPrefix}-resolved`}>
        {(() => { const { from, to } = resolveDateRange(value); return from || to ? `${from || "…"} → ${to || "…"}` : "All time"; })()}
      </span>
    </div>
  );
}

function StatutoryComplianceTab() {
  const [source, setSource] = useState("workmen");
  const [dateFilter, setDateFilter] = useState({ mode: "today" });
  const [rows, setRows] = useState([]);
  const [contractorsById, setContractorsById] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [contractorFilter, setContractorFilter] = useState("all");
  useEffect(() => { setSiteFilter("all"); setContractorFilter("all"); }, [source]);

  useEffect(() => {
    setLoading(true);
    let cancelled = false;
    if (source === "workmen") {
      Promise.all([
        api.get("/workmen/all"),
        api.get("/contractors", { params: { include_pending: true } }),
      ])
        .then(([wr, cr]) => {
          if (cancelled) return;
          setRows((wr.data || []).filter((w) => w.status === "approved"));
          const m = {};
          (cr.data || []).forEach((c) => { m[c.id] = c; });
          setContractorsById(m);
        })
        .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load workmen"))
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      api.get("/pay-roster", { params: { status: "approved" } })
        .then((r) => { if (!cancelled) setRows((r.data || []).filter((c) => c.pay_roster_status === "approved")); })
        .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load payroll records"))
        .finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => { cancelled = true; };
  }, [source]);

  const dateRange = useMemo(() => resolveDateRange(dateFilter), [dateFilter]);
  const approvalDateOf = (r) =>
    (source === "workmen" ? r.approved_at : (r.pay_roster_approved_at || r.approved_at)) || r.updated_at || r.created_at || "";
  const dateFiltered = useMemo(
    () => applyDateFilter(rows, dateRange, approvalDateOf),
    [rows, dateRange, source], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const contractorNameOf = (r) =>
    source === "workmen"
      ? (r.contractor_name || contractorsById[r.contractor_id]?.name || "—")
      : (r.source === "AGENCY" && r.source_agency_name ? `AGENCY · ${r.source_agency_name}` : "NCC Limited (Payroll)");
  const tradeOf = (r) => (source === "workmen" ? r.trade : ((r.trades && r.trades[0]) || r.contractor_type)) || "—";
  const aadhaarOf = (r) => {
    if (source === "workmen") return r.aadhaar || (String(r.id_proof_type || "").toLowerCase().startsWith("aadha") ? r.id_proof_number : "") || "—";
    return String(r.id_proof_type || "").toLowerCase().startsWith("aadha") ? (r.id_proof_number || "—") : "—";
  };
  const siteOf = (r) => ((r.ncc_site_name || (source === "workmen" ? contractorsById[r.contractor_id]?.ncc_site_name : "")) || "").trim();
  const siteOptions = useMemo(
    () => [...new Set(dateFiltered.map(siteOf).filter(Boolean))].sort(),
    [dateFiltered], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const contractorOptions = useMemo(
    () => [...new Set(dateFiltered.map((r) => contractorNameOf(r).trim()).filter(Boolean))].sort(),
    [dateFiltered, contractorsById, source], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const filtered = useMemo(() => {
    let rows2 = dateFiltered;
    if (siteFilter !== "all") rows2 = rows2.filter((r) => siteOf(r) === siteFilter);
    if (contractorFilter !== "all") rows2 = rows2.filter((r) => contractorNameOf(r).trim() === contractorFilter);
    const s = q.trim().toLowerCase();
    if (!s) return rows2;
    return rows2.filter((r) =>
      [r.name, r.id_card_no, siteOf(r), tradeOf(r), contractorNameOf(r), r.uan_no, r.esic_no, r.pf_no]
        .filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [q, dateFiltered, siteFilter, contractorFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const dGB = (s) => (s ? new Date(s).toLocaleDateString("en-GB") : "—");

  return (
    <div data-testid="statutory-tab">
      <div className="border-b-2 border-slate-900 pb-3">
        <div className="industrial-label text-[10px] text-slate-500">// Reports · Statutory Compliance</div>
        <h2 className="font-heading text-2xl font-black text-ncc-navy">Statutory Compliance</h2>
        <p className="mt-1 text-xs text-slate-600">Approved manpower with statutory identifiers (Aadhar / PF / UAN / ESIC) — filtered by approval date.</p>
      </div>
      <div className="mt-3 mb-3 flex flex-wrap items-center gap-3 border-l-4 border-ncc-gold bg-slate-100 px-4 py-3">
        <span className="industrial-label text-[10px] text-ncc-navy">// Site</span>
        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          data-testid="statutory-site-filter"
          className="max-w-[220px] border-2 border-slate-300 bg-white px-2 py-1.5 font-mono text-[12px] focus:border-ncc-navy focus:outline-none"
        >
          <option value="all">All Sites</option>
          {siteOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="industrial-label text-[10px] text-ncc-navy">// Contractor / Agency</span>
        <select
          value={contractorFilter}
          onChange={(e) => setContractorFilter(e.target.value)}
          data-testid="statutory-contractor-filter"
          className="max-w-[260px] border-2 border-slate-300 bg-white px-2 py-1.5 font-mono text-[12px] focus:border-ncc-navy focus:outline-none"
        >
          <option value="all">All Contractors / Agencies</option>
          {contractorOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-3 border-l-4 border-ncc-navy bg-slate-100 px-4 py-3">
        <span className="industrial-label text-[10px] text-ncc-navy">// Source</span>
        <div className="inline-flex overflow-hidden rounded-md border-2 border-ncc-navy">
          <button
            type="button"
            onClick={() => setSource("workmen")}
            data-testid="statutory-source-workmen"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 industrial-label text-xs transition-colors ${source === "workmen" ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            <UsersRound className="h-3.5 w-3.5" /> Workmen
          </button>
          <button
            type="button"
            onClick={() => setSource("payroll")}
            data-testid="statutory-source-payroll"
            className={`inline-flex items-center gap-1.5 border-l-2 border-ncc-navy px-4 py-1.5 industrial-label text-xs transition-colors ${source === "payroll" ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            <ClipboardList className="h-3.5 w-3.5" /> Payroll
          </button>
        </div>
        <span className="ml-auto font-mono text-[11px] text-slate-600" data-testid="statutory-count">
          {filtered.length} of {rows.length} approved record(s)
        </span>
      </div>
      <DateFilterBar value={dateFilter} onChange={setDateFilter} testidPrefix="statutory-date" />
      <div className="mt-2 flex items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, ID, trade, contractor, UAN, ESIC…"
          data-testid="statutory-search"
          className="w-full max-w-md border-2 border-slate-300 bg-white px-3 py-1.5 font-mono text-[12px] focus:border-ncc-navy focus:outline-none"
        />
      </div>
      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-ncc-navy" /></div>
      ) : (
        <div className="mt-3 overflow-x-auto border-2 border-slate-900 bg-white shadow-brutal-sm">
          <table className="w-full min-w-[1000px] text-left text-[12px]" data-testid="statutory-table">
            <thead>
              <tr className="bg-ncc-navy text-white">
                {["Sr.No.", "Site", "ID No.", `Name of ${source === "workmen" ? "Workmen" : "Employee"}`, "Name of Contractor", "Trade", "Date of Birth", "Aadhar No", "PF No.", "UAN No.", "ESIC No."].map((h) => (
                  <th key={h} className="whitespace-nowrap px-2 py-2 industrial-label text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-8 text-center font-mono text-[12px] text-slate-500">No approved records in this date range.</td></tr>
              )}
              {filtered.map((r, i) => (
                <tr key={r.id || i} className={i % 2 ? "bg-slate-50" : "bg-white"} data-testid={`statutory-row-${i}`}>
                  <td className="px-2 py-1.5 font-mono">{i + 1}</td>
                  <td className="px-2 py-1.5 text-[11px]">{siteOf(r) || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{r.id_card_no || "—"}</td>
                  <td className="px-2 py-1.5 font-semibold">{r.name || "—"}</td>
                  <td className="px-2 py-1.5">{contractorNameOf(r)}</td>
                  <td className="px-2 py-1.5">{tradeOf(r)}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{dGB(r.date_of_birth)}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{aadhaarOf(r)}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{r.pf_no || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{r.uan_no || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{r.esic_no || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WorkmenRosterTab() {
  // Top-level toggle: Workmen Roster vs Payroll Roster
  const [source, setSource] = useState("workmen");
  // Sub-report toggle (Quick View / Medical / Induction / Detailed)
  const [view, setView] = useState("summary");
  // Date filter (default: today)
  const [dateFilter, setDateFilter] = useState({ mode: "today" });
  // Bulk modal: which status bucket the admin is operating on
  const [bulkStatusKey, setBulkStatusKey] = useState(null);
  // Reload counter — bumped after a successful bulk action to force the hook to refetch
  const [reloadKey, setReloadKey] = useState(0);
  const workmenData = useWorkmenRosterData(dateFilter, reloadKey);
  const payRosterData = usePayRosterReportData(dateFilter, reloadKey);
  const baseData = source === "workmen" ? workmenData : payRosterData;
  // Site-wise / Contractor-wise filters
  const [siteFilter, setSiteFilter] = useState("all");
  const [contractorFilter, setContractorFilter] = useState("all");
  useEffect(() => { setSiteFilter("all"); setContractorFilter("all"); }, [source]);
  const contractorNameOf = (w) => w.contractor_name || baseData.contractorsById[w.contractor_id]?.name || "";
  const siteOptions = useMemo(
    () => [...new Set(baseData.workmen.map((w) => (w.ncc_site_name || "").trim()).filter(Boolean))].sort(),
    [baseData.workmen],
  );
  const contractorOptions = useMemo(
    () => [...new Set(baseData.workmen.map((w) => contractorNameOf(w).trim()).filter(Boolean))].sort(),
    [baseData.workmen, baseData.contractorsById],
  );
  const data = useMemo(() => {
    if (siteFilter === "all" && contractorFilter === "all") return baseData;
    const rows = baseData.workmen.filter((w) => {
      if (siteFilter !== "all" && (w.ncc_site_name || "").trim() !== siteFilter) return false;
      if (contractorFilter !== "all" && contractorNameOf(w).trim() !== contractorFilter) return false;
      return true;
    });
    return { ...baseData, workmen: rows };
  }, [baseData, siteFilter, contractorFilter]);
  const sourceLabel = source === "workmen" ? "Workmen Roster" : "Payroll Roster";
  const { can } = useAuth();
  const canBulk = !!can?.("approve_payroll_roster");
  return (
    <div data-testid="workmen-roster-tab">
      {/* Source toggle (Workmen / Payroll) */}
      <div className="mb-3 flex flex-wrap items-center gap-3 border-l-4 border-ncc-navy bg-slate-100 px-4 py-3">
        <span className="industrial-label text-[10px] text-ncc-navy">// Source</span>
        <div className="inline-flex overflow-hidden rounded-md border-2 border-ncc-navy">
          <button
            type="button"
            onClick={() => setSource("workmen")}
            data-testid="reports-roster-source-workmen"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 industrial-label text-xs transition-colors ${
              source === "workmen" ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <UsersRound className="h-3.5 w-3.5" /> Workmen Roster
          </button>
          <button
            type="button"
            onClick={() => setSource("payroll")}
            data-testid="reports-roster-source-payroll"
            className={`inline-flex items-center gap-1.5 border-l-2 border-ncc-navy px-4 py-1.5 industrial-label text-xs transition-colors ${
              source === "payroll" ? "bg-ncc-navy text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <ClipboardList className="h-3.5 w-3.5" /> Payroll Roster
          </button>
        </div>
        <span className="ml-auto font-mono text-[11px] text-slate-600" data-testid="reports-roster-source-count">
          Showing <b>{sourceLabel}</b> · {data.workmen.length} of {data.total} record(s)
        </span>
      </div>

      {/* Date filter — Daily (default) / Weekly / Monthly / As On / Date Range */}
      <DateFilterBar value={dateFilter} onChange={setDateFilter} testidPrefix="reports-roster-date" />

      {/* Site-wise / Contractor-wise filters */}
      <div className="mt-2 flex flex-wrap items-center gap-3 border-l-4 border-ncc-gold bg-slate-100 px-4 py-2.5" data-testid="reports-roster-filters">
        <span className="industrial-label text-[10px] text-ncc-navy">// Filters</span>
        <label className="flex items-center gap-2">
          <span className="industrial-label text-[10px] text-slate-600">Site Wise</span>
          <select
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            data-testid="reports-roster-filter-site"
            className="max-w-[220px] border-2 border-ncc-navy bg-white px-2 py-1 font-mono text-[12px] focus:outline-none"
          >
            <option value="all">All Sites ({siteOptions.length})</option>
            {siteOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="industrial-label text-[10px] text-slate-600">Contractor Wise</span>
          <select
            value={contractorFilter}
            onChange={(e) => setContractorFilter(e.target.value)}
            data-testid="reports-roster-filter-contractor"
            className="max-w-[220px] border-2 border-ncc-navy bg-white px-2 py-1 font-mono text-[12px] focus:outline-none"
          >
            <option value="all">All Contractors ({contractorOptions.length})</option>
            {contractorOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        {(siteFilter !== "all" || contractorFilter !== "all") && (
          <button
            type="button"
            onClick={() => { setSiteFilter("all"); setContractorFilter("all"); }}
            data-testid="reports-roster-filter-clear"
            className="inline-flex items-center gap-1 rounded border border-red-300 bg-red-50 px-2 py-1 font-mono text-[11px] text-red-700 hover:bg-red-100"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
        <span className="ml-auto font-mono text-[10px] text-slate-500" data-testid="reports-roster-filter-count">
          {data.workmen.length} record(s) match
        </span>
      </div>

      {/* Status breakdown for Payroll Roster — explains why the dashboard "Pay Roster Approved"
          may not equal the total Payroll Roster count (the report shows ALL payroll records,
          while the KPI counts only pay_roster_status='approved'). Each chip is also a button:
          clicking opens a Bulk Approve / Reject modal pre-filtered to that status bucket. */}
      {source === "payroll" && data.statusCounts && (
        <div className="mt-2 flex flex-wrap items-center gap-2 border-l-4 border-amber-400 bg-amber-50 px-3 py-2" data-testid="reports-pay-roster-status-breakdown">
          <span className="industrial-label text-[10px] text-amber-800">// Pay-Roster Status Breakdown</span>
          {[
            ["approved", "Approved", "bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200"],
            ["pending", "Pending", "bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200"],
            ["rejected", "Rejected", "bg-red-100 text-red-700 border-red-300 hover:bg-red-200"],
            ["not_submitted", "Not Submitted", "bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200"],
          ].map(([key, label, cls]) => (
            <button
              key={key}
              type="button"
              onClick={() => canBulk && setBulkStatusKey(key)}
              disabled={!canBulk}
              title={canBulk ? `Open ${label} — bulk approve / reject` : "Requires Approve Pay Roster permission"}
              className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[11px] transition-colors ${cls} ${canBulk ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
              data-testid={`reports-pay-roster-count-${key}`}
            >
              <b>{data.statusCounts[key] || 0}</b> {label}
            </button>
          ))}
          <span className="ml-auto font-mono text-[10px] text-slate-500">
            Dashboard KPI counts <b>Approved</b> only. {canBulk ? "Click a chip to bulk action." : ""}
          </span>
        </div>
      )}

      {/* Bulk Approve / Reject modal — fires onCompleted to force data hooks to refresh */}
      <PayRosterBulkModal
        open={!!bulkStatusKey}
        statusKey={bulkStatusKey}
        onClose={() => setBulkStatusKey(null)}
        onCompleted={() => setReloadKey((k) => k + 1)}
      />

      <div className="mt-3" />

      <ReportSubToggle
        value={view}
        onChange={setView}
        options={[
          { id: "summary",   label: `${sourceLabel} · Quick View` },
          { id: "medical",   label: "Medical Report (Form-XI)" },
          { id: "induction", label: "Induction Report (Screening Form)" },
          { id: "detailed",  label: `Detailed ${sourceLabel} · Form-XI Compliance` },
        ]}
        testidPrefix="workmen-roster-subtoggle"
      />
      {view === "summary"   && <WorkmenRosterSummaryReport data={data} />}
      {view === "medical"   && <WorkmenMedicalReport data={data} />}
      {view === "induction" && <WorkmenInductionReport data={data} />}
      {view === "detailed"  && <DetailedWorkmenRosterReport data={data} />}
    </div>
  );
}

// Cached fetch shared by both sub-reports — also used for the contractor name lookup.
// Accepts an optional dateFilter (see resolveDateRange()) — applied to workman.created_at.
function useWorkmenRosterData(dateFilter, reloadKey) {
  const [workmen, setWorkmen] = useState([]);
  const [contractorsById, setContractorsById] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/workmen/all"),
      api.get("/contractors", { params: { include_pending: true } }),
    ])
      .then(([wr, cr]) => {
        setWorkmen(wr.data || []);
        const map = {};
        (cr.data || []).forEach((c) => { map[c.id] = c; });
        setContractorsById(map);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load workmen"))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  // Apply date filter first, then text search.
  const dateRange = useMemo(() => resolveDateRange(dateFilter), [dateFilter]);
  const dateFiltered = useMemo(
    () => applyDateFilter(workmen, dateRange, (w) => w.created_at || w.updated_at || ""),
    [workmen, dateRange],
  );
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return dateFiltered;
    return dateFiltered.filter((w) => {
      const c = contractorsById[w.contractor_id];
      return [w.name, w.father_husband_name, w.trade, w.mobile, w.ncc_site_name, w.aadhaar, w.id_proof_number, w.village, w.city, w.state, c?.name]
        .filter(Boolean).join(" ").toLowerCase().includes(s);
    });
  }, [q, dateFiltered, contractorsById]);

  return { workmen: filtered, contractorsById, loading, q, setQ, total: workmen.length, dateRange };
}

// Payroll Roster sibling of useWorkmenRosterData — derives a Workman-shaped row
// list from approved Payroll Manpower records so the 4 sub-reports work unchanged.
function usePayRosterReportData(dateFilter, reloadKey) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/pay-roster")
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load pay roster"))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  // Map every payroll record to a Workman-shaped row. The contractorsById map
  // doubles back to the original contractor so per-contractor groupings still work.
  const workmen = useMemo(() => rows.map(_payrollContractorToWorkman), [rows]);
  const contractorsById = useMemo(() => {
    const m = {};
    rows.forEach((c) => { m[c.id] = c; });
    return m;
  }, [rows]);

  // Status breakdown so the user can see why /api/pay-roster (e.g. 154) doesn't equal
  // the dashboard "Pay Roster Approved" KPI (e.g. 7) — the difference is pending /
  // rejected / not-yet-submitted records.
  const statusCounts = useMemo(() => {
    const c = { approved: 0, pending: 0, rejected: 0, not_submitted: 0 };
    rows.forEach((r) => {
      const s = r.pay_roster_status || "not_submitted";
      if (c[s] !== undefined) c[s] += 1;
      else c.not_submitted += 1;
    });
    return c;
  }, [rows]);

  // Apply date filter first (uses pay_roster_submitted_at when present, else created_at),
  // then text search.
  const dateRange = useMemo(() => resolveDateRange(dateFilter), [dateFilter]);
  const dateFiltered = useMemo(
    () => applyDateFilter(workmen, dateRange, (w) => w.created_at || ""),
    [workmen, dateRange],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return dateFiltered;
    return dateFiltered.filter((w) => {
      const c = contractorsById[w.contractor_id];
      return [w.name, w.father_husband_name, w.trade, w.mobile, w.ncc_site_name, w.aadhaar, w.id_proof_number, w.village, w.city, w.state, c?.name]
        .filter(Boolean).join(" ").toLowerCase().includes(s);
    });
  }, [q, dateFiltered, contractorsById]);

  return { workmen: filtered, contractorsById, loading, q, setQ, total: workmen.length, statusCounts, dateRange };
}

// (A) Detailed Workmen Roster — mirrors NCC_SWMS_Detailed_Workmen_Roster_Template.xlsx (56 cols)
function DetailedWorkmenRosterReport({ data }) {
  const { workmen, contractorsById, loading, q, setQ, total } = data;

  // 2-row header to match the template's grouped structure
  const personalSubs = [
    "Full Name", "Father/Husband Name", "DOB", "Age", "Mobile", "Gender",
    "ID Proof Type", "Aadhaar Number", "Address", "Pincode", "Village", "City/District", "State",
    "Blood Group", "Nationality", "Religion", "Category", "Trade", "Experience (Yrs)",
    "Marital Status", "No. of Children", "Mother Tongue", "Other Languages",
    "Emergency Contact Name", "Emergency Contact Number",
  ];
  const physicalSubs = ["Vision", "Hearing", "Height (cm)", "Weight (kg)", "Physical Deformities", "Medical History"];
  const examSubs = ["Blood Pressure", "Pulse", "Refractive Error", "Colour Vision", "Disability", "Arm Function & Grip", "Leg & Foot Function"];
  const historySubs = ["Varicose", "Seizure", "Vertigo", "Acrophobia", "Diabetes", "Stroke", "Heart Diseases", "Major Illness/Surgery", "Symptoms Visible", "Others"];
  const tailSubs = ["Medical Fitness", "Fitness Date", "Medical Expiry", "Ready to Mobilize", "Registration Date"];

  function dateGB(s) { return s ? new Date(s).toLocaleDateString("en-GB") : ""; }

  return (
    <div data-testid="workmen-roster-detailed">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-900 bg-ncc-navy px-5 py-4">
        <div>
          <div className="industrial-label text-ncc-gold2">// Detail</div>
          <h3 className="mt-0.5 font-heading text-xl font-black text-white">Detailed Workmen Roster · Form-XI Compliance</h3>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, mobile, trade, site…"
          data-testid="wmr-detailed-search"
          className="w-64 border-2 border-ncc-gold bg-white px-3 py-2 font-mono text-sm focus:outline-none"
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 border-x-2 border-slate-900 bg-slate-50 px-5 py-3">
        <span className="industrial-label text-ncc-navy">// Roster</span>
        <span className="ml-auto font-mono text-[11px] text-slate-600">
          Showing <b>{workmen.length}</b> of <b>{total}</b> workmen
        </span>
      </div>
      <div className="border-x-2 border-b-2 border-slate-900 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-ncc-navy text-white">
              <tr>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Workman ID</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Contractor</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Type of Contractor</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Project</th>
                <th colSpan={personalSubs.length} className="px-2 py-2 text-center industrial-label bg-ncc-navy/95">Personal Details</th>
                <th colSpan={physicalSubs.length} className="px-2 py-2 text-center industrial-label bg-emerald-700">Physical Condition</th>
                <th colSpan={examSubs.length} className="px-2 py-2 text-center industrial-label bg-emerald-800">Physical Examination</th>
                <th colSpan={historySubs.length} className="px-2 py-2 text-center industrial-label bg-emerald-900">Enquiry of Previous History</th>
                <th colSpan={tailSubs.length} className="px-2 py-2 text-center industrial-label bg-ncc-navy">Compliance / Status</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Remarks</th>
              </tr>
              <tr className="bg-slate-100 text-slate-700">
                {[...personalSubs, ...physicalSubs, ...examSubs, ...historySubs, ...tailSubs].map((h) => (
                  <th key={h} className="border-l border-slate-300 px-2 py-1.5 text-left industrial-label text-[10px] font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={58} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
              ) : workmen.length === 0 ? (
                <tr><td colSpan={57} className="p-6 text-center font-mono text-slate-500">No workmen registered yet.</td></tr>
              ) : workmen.map((w) => {
                const c = contractorsById[w.contractor_id];
                const aadhaarOrId = w.id_proof_number || w.aadhaar || "";
                const medFit = w.status === "approved" ? "Fit" : (w.status === "rejected" ? "Unfit" : "Pending");
                const fitnessDate = w.status === "approved" && w.approved_at ? dateGB(w.approved_at) : "";
                const expiry = fitnessDate
                  ? new Date(new Date(w.approved_at).getTime() + 365 * 24 * 3600 * 1000).toLocaleDateString("en-GB")
                  : "";
                const ready = w.is_active !== false && w.status === "approved" ? "Yes" : "No";
                return (
                  <tr key={w.id} className="border-t border-slate-200 align-top hover:bg-slate-50" data-testid={`wmr-detailed-row-${w.id}`}>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{w.id_card_no || w.id?.slice(0, 8) || ""}</td>
                    <td className="px-2 py-1.5 font-bold text-slate-900">{c?.name || "—"}</td>
                    <td className="px-2 py-1.5">{w.ncc_site_name || c?.ncc_site_name || ""}</td>
                    {/* Personal */}
                    <td className="px-2 py-1.5 font-bold">{w.name || ""}</td>
                    <td className="px-2 py-1.5">{w.father_husband_name || ""}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{dateGB(w.date_of_birth)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{w.age_years ?? ""}</td>
                    <td className="px-2 py-1.5 font-mono">{w.mobile || ""}</td>
                    <td className="px-2 py-1.5">{w.gender || ""}</td>
                    <td className="px-2 py-1.5">{w.id_proof_type || ""}</td>
                    <td className="px-2 py-1.5 font-mono">{aadhaarOrId}</td>
                    <td className="px-2 py-1.5">{w.address || ""}</td>
                    <td className="px-2 py-1.5 font-mono">{w.pincode || ""}</td>
                    <td className="px-2 py-1.5">{w.village || ""}</td>
                    <td className="px-2 py-1.5">{w.city || ""}</td>
                    <td className="px-2 py-1.5">{w.state || ""}</td>
                    <td className="px-2 py-1.5 font-bold text-red-700">{w.blood_group || ""}</td>
                    <td className="px-2 py-1.5">{w.nationality || "Indian"}</td>
                    <td className="px-2 py-1.5">{w.religion || ""}</td>
                    <td className="px-2 py-1.5">{w.category || ""}</td>
                    <td className="px-2 py-1.5 font-bold">{w.trade || ""}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{w.experience_years ?? ""}</td>
                    <td className="px-2 py-1.5">{w.marital_status || ""}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{w.children_count ?? ""}</td>
                    <td className="px-2 py-1.5">{w.mother_tongue || ""}</td>
                    <td className="px-2 py-1.5">{w.other_languages || ""}</td>
                    <td className="px-2 py-1.5">{w.emergency_contact_name || ""}</td>
                    <td className="px-2 py-1.5 font-mono">{w.emergency_contact_number || ""}</td>
                    {/* Physical condition */}
                    <td className="px-2 py-1.5">{w.vision || ""}</td>
                    <td className="px-2 py-1.5">{w.hearing || ""}</td>
                    <td className="px-2 py-1.5">{w.height || ""}</td>
                    <td className="px-2 py-1.5">{w.weight || ""}</td>
                    <td className="px-2 py-1.5">{w.physical_deformities || ""}</td>
                    <td className="px-2 py-1.5">{w.medical_history || ""}</td>
                    {/* Physical Examination */}
                    <td className="px-2 py-1.5">{w.phys_exam_blood_pressure || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_pulse || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_refractive_error || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_colour_vision || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_disability || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_arm_function || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_leg_function || ""}</td>
                    {/* History */}
                    <td className="px-2 py-1.5">{w.hist_varicose || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_seizure || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_vertigo || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_acrophobia || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_diabetes || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_stroke || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_heart_diseases || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_major_illness_surgery || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_symptoms_visible || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_others || ""}</td>
                    {/* Compliance / Status */}
                    <td className={`px-2 py-1.5 font-bold ${medFit === "Fit" ? "text-emerald-700" : medFit === "Unfit" ? "text-red-700" : "text-amber-700"}`}>{medFit}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{fitnessDate}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{expiry}</td>
                    <td className={`px-2 py-1.5 font-bold ${ready === "Yes" ? "text-emerald-700" : "text-slate-500"}`}>{ready}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{dateGB(w.created_at)}</td>
                    {/* Remarks */}
                    <td className="px-2 py-1.5">{w.reject_reason || w.bio || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-3 font-mono text-[11px] text-slate-500">
        56-column compliance template matching <b>NCC_SWMS_Detailed_Workmen_Roster</b>. Use the <b>Export Excel</b> button above to download.
      </p>
    </div>
  );
}

// (B1) Medical Report (Form-XI) — Physical Examination + Enquiry of Previous History
function WorkmenMedicalReport({ data }) {
  const { workmen, contractorsById, loading, q, setQ, total } = data;
  return (
    <div data-testid="workmen-roster-medical">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-900 bg-ncc-navy px-5 py-4">
        <div>
          <div className="industrial-label text-ncc-gold2">// Form-XI</div>
          <h3 className="mt-0.5 font-heading text-xl font-black text-white">Medical Report · Form-XI (BOCW Rule 223(c))</h3>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, mobile, trade…"
          data-testid="wmr-medical-search"
          className="w-64 border-2 border-ncc-gold bg-white px-3 py-2 font-mono text-sm focus:outline-none"
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 border-x-2 border-slate-900 bg-slate-50 px-5 py-3">
        <span className="industrial-label text-ncc-navy">// Medical</span>
        <span className="ml-auto font-mono text-[11px] text-slate-600">
          Showing <b>{workmen.length}</b> of <b>{total}</b> workmen
        </span>
      </div>
      <div className="border-x-2 border-b-2 border-slate-900 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-ncc-navy text-white">
              <tr>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Workman ID</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Name</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Contractor</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Type of Contractor</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Trade</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Age</th>
                <th colSpan={7} className="px-2 py-2 text-center industrial-label bg-emerald-800">Physical Examination</th>
                <th colSpan={10} className="px-2 py-2 text-center industrial-label bg-emerald-900">Enquiry of Previous History</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Medical Fitness</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Fitness Date</th>
                <th rowSpan={2} className="px-2 py-2 text-left industrial-label">Medical Expiry</th>
              </tr>
              <tr className="bg-slate-100 text-slate-700">
                {["Blood Pressure", "Pulse", "Refractive Error", "Colour Vision", "Disability", "Arm Function & Grip", "Leg & Foot Function",
                  "Varicose", "Seizure", "Vertigo", "Acrophobia", "Diabetes", "Stroke", "Heart Diseases", "Major Illness/Surgery", "Symptoms Visible", "Others"]
                  .map((h) => (
                    <th key={h} className="border-l border-slate-300 px-2 py-1.5 text-left industrial-label text-[10px] font-bold">{h}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={26} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
              ) : workmen.length === 0 ? (
                <tr><td colSpan={26} className="p-6 text-center font-mono text-slate-500">No workmen registered yet.</td></tr>
              ) : workmen.map((w) => {
                const c = contractorsById[w.contractor_id];
                const fit = w.status === "approved" ? "Fit" : w.status === "rejected" ? "Unfit" : "Pending";
                const fitnessDate = w.status === "approved" && w.approved_at ? new Date(w.approved_at).toLocaleDateString("en-GB") : "";
                const expiry = fitnessDate ? new Date(new Date(w.approved_at).getTime() + 365 * 24 * 3600 * 1000).toLocaleDateString("en-GB") : "";
                return (
                  <tr key={w.id} className="border-t border-slate-200 align-top hover:bg-slate-50" data-testid={`wmr-medical-row-${w.id}`}>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{w.id_card_no || w.id?.slice(0, 8) || ""}</td>
                    <td className="px-2 py-1.5 font-bold">{w.name}</td>
                    <td className="px-2 py-1.5">{c?.name || "—"}</td>
                    <td className="px-2 py-1.5">{c?.contractor_type || "—"}</td>
                    <td className="px-2 py-1.5">{w.trade || ""}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{w.age_years ?? ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_blood_pressure || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_pulse || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_refractive_error || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_colour_vision || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_disability || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_arm_function || ""}</td>
                    <td className="px-2 py-1.5">{w.phys_exam_leg_function || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_varicose || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_seizure || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_vertigo || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_acrophobia || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_diabetes || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_stroke || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_heart_diseases || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_major_illness_surgery || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_symptoms_visible || ""}</td>
                    <td className="px-2 py-1.5">{w.hist_others || ""}</td>
                    <td className={`px-2 py-1.5 font-bold ${fit === "Fit" ? "text-emerald-700" : fit === "Unfit" ? "text-red-700" : "text-amber-700"}`}>{fit}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{fitnessDate}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{expiry}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-3 font-mono text-[11px] text-slate-500">
        Mirrors the Form-XI medical certificate fields (BOCW Rule 223(c)) — useful for site doctor audits.
      </p>
    </div>
  );
}

// (B2) Induction Report — fields tracked on the Worker's Screening Form
function WorkmenInductionReport({ data }) {
  const { workmen, contractorsById, loading, q, setQ, total } = data;
  return (
    <div data-testid="workmen-roster-induction">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-900 bg-ncc-navy px-5 py-4">
        <div>
          <div className="industrial-label text-ncc-gold2">// Screening</div>
          <h3 className="mt-0.5 font-heading text-xl font-black text-white">Induction Report · Worker&apos;s Screening Form</h3>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, mobile, trade…"
          data-testid="wmr-induction-search"
          className="w-64 border-2 border-ncc-gold bg-white px-3 py-2 font-mono text-sm focus:outline-none"
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 border-x-2 border-slate-900 bg-slate-50 px-5 py-3">
        <span className="industrial-label text-ncc-navy">// Induction</span>
        <span className="ml-auto font-mono text-[11px] text-slate-600">
          Showing <b>{workmen.length}</b> of <b>{total}</b> workmen
        </span>
      </div>
      <div className="border-x-2 border-b-2 border-slate-900 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-ncc-navy text-white">
              <tr>
                {[
                  "Workman ID", "Name", "Contractor", "Type of Contractor",
                  "DOB", "Age", "Gender", "Blood Group", "ID Proof", "Trade", "Experience",
                  "Address", "Marital Status", "Children", "Mother Tongue", "Other Languages",
                  "Emergency Contact",
                  "Vision", "Height", "Weight", "Hearing", "Physical Deformities", "Medical History",
                  "Induction Status",
                ].map((h) => (
                  <th key={h} className="px-2 py-2 text-left industrial-label">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={24} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
              ) : workmen.length === 0 ? (
                <tr><td colSpan={24} className="p-6 text-center font-mono text-slate-500">No workmen registered yet.</td></tr>
              ) : workmen.map((w) => {
                const c = contractorsById[w.contractor_id];
                const induction = w.status === "approved" ? "Cleared" : w.status === "rejected" ? "Rejected" : "Pending";
                const dob = w.date_of_birth ? new Date(w.date_of_birth).toLocaleDateString("en-GB") : "";
                const idProof = [w.id_proof_type, w.id_proof_number || w.aadhaar].filter(Boolean).join(": ");
                const addr = [w.address, w.village, w.city, w.state, w.pincode].filter(Boolean).join(", ");
                const emerg = [w.emergency_contact_name, w.emergency_contact_number].filter(Boolean).join(" · ");
                return (
                  <tr key={w.id} className="border-t border-slate-200 align-top hover:bg-slate-50" data-testid={`wmr-induction-row-${w.id}`}>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{w.id_card_no || w.id?.slice(0, 8) || ""}</td>
                    <td className="px-2 py-1.5 font-bold">{w.name}</td>
                    <td className="px-2 py-1.5">{c?.name || "—"}</td>
                    <td className="px-2 py-1.5">{c?.contractor_type || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{dob}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{w.age_years ?? ""}</td>
                    <td className="px-2 py-1.5">{w.gender || ""}</td>
                    <td className="px-2 py-1.5 font-bold text-red-700">{w.blood_group || ""}</td>
                    <td className="px-2 py-1.5 font-mono">{idProof}</td>
                    <td className="px-2 py-1.5 font-bold">{w.trade || ""}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{w.experience_years ?? ""}</td>
                    <td className="px-2 py-1.5">{addr}</td>
                    <td className="px-2 py-1.5">{w.marital_status || ""}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{w.children_count ?? ""}</td>
                    <td className="px-2 py-1.5">{w.mother_tongue || ""}</td>
                    <td className="px-2 py-1.5">{w.other_languages || ""}</td>
                    <td className="px-2 py-1.5">{emerg}</td>
                    <td className="px-2 py-1.5">{w.vision || ""}</td>
                    <td className="px-2 py-1.5">{w.height || ""}</td>
                    <td className="px-2 py-1.5">{w.weight || ""}</td>
                    <td className="px-2 py-1.5">{w.hearing || ""}</td>
                    <td className="px-2 py-1.5">{w.physical_deformities || ""}</td>
                    <td className="px-2 py-1.5">{w.medical_history || ""}</td>
                    <td className={`px-2 py-1.5 font-bold ${induction === "Cleared" ? "text-emerald-700" : induction === "Rejected" ? "text-red-700" : "text-amber-700"}`}>{induction}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-3 font-mono text-[11px] text-slate-500">
        Tracks the Worker&apos;s Screening Form fields used during HSE induction at site.
      </p>
    </div>
  );
}

// (B) Workmen Roster — short 13-col view matching roster.xlsx
function WorkmenRosterSummaryReport({ data }) {
  const { workmen, contractorsById, loading, q, setQ, total } = data;

  return (
    <div data-testid="workmen-roster-summary">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-900 bg-ncc-navy px-5 py-4">
        <div>
          <div className="industrial-label text-ncc-gold2">// Summary</div>
          <h3 className="mt-0.5 font-heading text-xl font-black text-white">Workmen Roster · Quick View</h3>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, mobile, trade…"
          data-testid="wmr-summary-search"
          className="w-64 border-2 border-ncc-gold bg-white px-3 py-2 font-mono text-sm focus:outline-none"
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 border-x-2 border-slate-900 bg-slate-50 px-5 py-3">
        <span className="industrial-label text-ncc-navy">// Roster</span>
        <span className="ml-auto font-mono text-[11px] text-slate-600">
          Showing <b>{workmen.length}</b> of <b>{total}</b> workmen
        </span>
      </div>
      <div className="border-x-2 border-b-2 border-slate-900 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ncc-navy text-white">
              <tr>
                {["Photo ID", "Contractor Name", "Type of Contractor", "Name", "Trade", "Mobile", "Pincode", "Village", "District", "State", "Age", "Medical", "Induction", "Status"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left industrial-label">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={14} className="p-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>
              ) : workmen.length === 0 ? (
                <tr><td colSpan={14} className="p-6 text-center font-mono text-slate-500">No workmen registered yet.</td></tr>
              ) : workmen.map((w) => {
                const c = contractorsById[w.contractor_id];
                const med = w.status === "approved" ? "Fit" : w.status === "rejected" ? "Unfit" : "Pending";
                const induction = w.status === "approved" ? "Cleared" : "Pending";
                const liveStatus = w.is_active === false
                  ? `DE-ACTIVE${w.last_working_day ? ` · ${new Date(w.last_working_day).toLocaleDateString("en-GB")}` : ""}`
                  : (w.status || "pending").toUpperCase();
                return (
                  <tr key={w.id} className="border-t border-slate-200 align-top hover:bg-slate-50" data-testid={`wmr-summary-row-${w.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{w.id_card_no || w.id?.slice(0, 8) || ""}</td>
                    <td className="px-3 py-2 font-bold">{c?.name || "—"}</td>
                    <td className="px-3 py-2">{c?.contractor_type || "—"}</td>
                    <td className="px-3 py-2 font-bold text-slate-900">{w.name}</td>
                    <td className="px-3 py-2">{w.trade || "—"}</td>
                    <td className="px-3 py-2 font-mono">{w.mobile || "—"}</td>
                    <td className="px-3 py-2 font-mono">{w.pincode || ""}</td>
                    <td className="px-3 py-2">{w.village || ""}</td>
                    <td className="px-3 py-2">{w.city || ""}</td>
                    <td className="px-3 py-2">{w.state || ""}</td>
                    <td className="px-3 py-2 text-right font-mono">{w.age_years ?? ""}</td>
                    <td className={`px-3 py-2 font-bold ${med === "Fit" ? "text-emerald-700" : med === "Unfit" ? "text-red-700" : "text-amber-700"}`}>{med}</td>
                    <td className={`px-3 py-2 font-bold ${induction === "Cleared" ? "text-emerald-700" : "text-amber-700"}`}>{induction}</td>
                    <td className="px-3 py-2 font-bold">{liveStatus}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-3 font-mono text-[11px] text-slate-500">
        Quick-view 13-column roster matching <b>roster.xlsx</b>. Use <b>Export Excel</b> above to download.
      </p>
    </div>
  );
}

// ---------- Reusable Report Action Bar ----------
// Provides Export Excel · Import Excel · Print buttons available in every tab.
// Print uses native window.print() (use the page's print stylesheet).
// Export Excel — for tabs with an existing backend endpoint, hits that URL;
//   for tabs without one (Registration Details, Contractor NCC, Payroll NCC,
//   New Contractors, New Payroll, Directory) it builds an .xls from the
//   currently-rendered <table> in DOM via HTML-Excel trick.
// Import Excel — directory has its own importer; for other tabs we jump to
//   Directory's import flow (only raw-data import lives there).
function ReportActions({ tab, onJumpDirectory }) {
  const EXPORT_URLS = {
    summary: "/reports/manpower-summary/export-excel",
    workforce: "/reports/workforce/export-excel",
    funnel: "/reports/funnel/export-excel",
    "ncc-sites": "/reports/ncc-sites/export-excel",
    ready: "/reports/ready-to-mobilize/export-excel",
  };
  const [busy, setBusy] = useState(false);

  function exportFromDOM() {
    // Find the first visible <table> within the report content (excluding the action bar itself).
    const tables = Array.from(document.querySelectorAll(".reports-content table"));
    const table = tables.find((t) => t.offsetParent !== null);
    if (!table) {
      toast.error("Nothing to export from this view");
      return;
    }
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>${tab}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body>${table.outerHTML}</body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tab}-${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Excel downloaded");
  }

  async function exportFromBackend(url) {
    setBusy(true);
    try {
      const r = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${tab}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success("Excel downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  }

  function handleExport() {
    const url = EXPORT_URLS[tab];
    if (url) exportFromBackend(url);
    else exportFromDOM();
  }

  function handleImport() {
    if (tab === "directory") {
      // Trigger the existing directory import button if visible
      const btn = document.querySelector('[data-testid="import-contractor-btn"]');
      if (btn) { btn.click(); return; }
    }
    toast.info("Imports happen in Directory · navigating you there");
    onJumpDirectory?.();
  }

  function handlePrint() {
    window.print();
  }

  return (
    <div className="mb-6 flex flex-wrap items-center justify-end gap-2 border-2 border-slate-900 bg-white px-4 py-2.5 shadow-brutal-sm print:hidden" data-testid="report-actions-bar">
      <div className="mr-auto industrial-label text-[10px] text-slate-500">// Actions</div>
      <button onClick={handleExport} disabled={busy} data-testid="rpt-action-export"
        className="inline-flex items-center gap-1.5 border-2 border-ncc-navy bg-ncc-gold px-3 py-1.5 industrial-label text-ncc-navy hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-60">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        Export Excel
      </button>
      <button onClick={handleImport} data-testid="rpt-action-import"
        className="inline-flex items-center gap-1.5 border-2 border-ncc-navy bg-white px-3 py-1.5 industrial-label text-ncc-navy hover:-translate-y-0.5 hover:bg-ncc-tint hover:shadow-brutal-sm">
        <Upload className="h-3.5 w-3.5" /> Import Excel
      </button>
      <button onClick={handlePrint} data-testid="rpt-action-print"
        className="inline-flex items-center gap-1.5 border-2 border-slate-900 bg-white px-3 py-1.5 industrial-label text-slate-900 hover:-translate-y-0.5 hover:bg-slate-100 hover:shadow-brutal-sm">
        <Printer className="h-3.5 w-3.5" /> Print
      </button>
    </div>
  );
}

// ---------- Shared helpers ----------
function DateRange({ from, to, setFrom, setTo, onClear }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label className="industrial-label text-slate-500">From</label>
        <div className="mt-1 flex items-center border-2 border-slate-900 bg-white px-3 py-2">
          <Calendar className="mr-2 h-4 w-4 text-slate-500" />
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from-date" className="font-mono text-sm focus:outline-none" />
        </div>
      </div>
      <div>
        <label className="industrial-label text-slate-500">To</label>
        <div className="mt-1 flex items-center border-2 border-slate-900 bg-white px-3 py-2">
          <Calendar className="mr-2 h-4 w-4 text-slate-500" />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to-date" className="font-mono text-sm focus:outline-none" />
        </div>
      </div>
      {(from || to) && (
        <button onClick={onClear} data-testid="report-clear-dates" className="border-2 border-slate-900 bg-white px-3 py-2 industrial-label hover:bg-slate-100">
          <RefreshCw className="mr-2 inline h-3.5 w-3.5" /> Clear
        </button>
      )}
    </div>
  );
}

function ExportBtn({ onClick, busy, testid, label = "Export Excel" }) {
  return (
    <button onClick={onClick} disabled={busy} data-testid={testid}
      className="inline-flex items-center gap-2 border-2 border-slate-900 bg-ncc-gold px-4 py-2 industrial-label text-ncc-navy transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-50">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {label}
    </button>
  );
}

async function downloadExcel(path, filename, setBusy) {
  setBusy(true);
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Download failed");
  } finally {
    setBusy(false);
  }
}

function SegmentToggle({ value, onChange, testidPrefix }) {
  return (
    <div className="inline-flex border-2 border-slate-900 bg-white">
      <button onClick={() => onChange("contractor")} data-testid={`${testidPrefix}-contractor`}
        className={`px-4 py-2 industrial-label transition-colors ${value === "contractor" ? "bg-ncc-gold text-ncc-navy" : "text-slate-700 hover:bg-slate-100"}`}>Contractor</button>
      <button onClick={() => onChange("payroll")} data-testid={`${testidPrefix}-payroll`}
        className={`border-l-2 border-slate-900 px-4 py-2 industrial-label transition-colors ${value === "payroll" ? "bg-blueprint text-white" : "text-slate-700 hover:bg-slate-100"}`}>Payroll Manpower</button>
    </div>
  );
}

function InlineGeoEditor({ p, field, onSaved }) {
  const label = field.charAt(0).toUpperCase() + field.slice(1);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(p[field] || "");
  const [busy, setBusy] = useState(false);

  async function save() {
    const v = val.trim();
    if (!v) { toast.error(`Enter a ${field} name`); return; }
    setBusy(true);
    try {
      await api.patch(`/contractors/${p.id}/geo`, { [field]: v });
      toast.success(`${label} "${v}" saved for ${p.name}`);
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to save ${field}`);
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <button
        onClick={() => { setVal(p[field] || ""); setEditing(true); }}
        data-testid={`${field}-edit-btn-${p.id}`}
        className={`inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono text-[11px] hover:bg-ncc-gold/30 ${p[field] ? "border-slate-300 text-slate-700" : "border-dashed border-slate-400 text-blueprint"}`}
        title={p[field] ? `Edit ${field}` : `Add ${field}`}
      >
        {p[field] ? (<>{p[field]} <Pencil className="h-3 w-3" /></>) : (<>+ Add {label}</>)}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        placeholder={`${label} name`}
        data-testid={`${field}-input-${p.id}`}
        className="w-28 border border-slate-400 bg-white px-1.5 py-0.5 font-mono text-[11px] focus:border-ncc-navy focus:outline-none"
      />
      <button onClick={save} disabled={busy} data-testid={`${field}-save-${p.id}`} className="border border-emerald-700 bg-emerald-50 p-0.5 text-emerald-700 hover:bg-emerald-100" title="Save">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
      </button>
      <button onClick={() => setEditing(false)} disabled={busy} data-testid={`${field}-cancel-${p.id}`} className="border border-slate-300 p-0.5 text-slate-500 hover:bg-slate-100" title="Cancel">
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

function BreakdownTable({ title, rows, icon, testid, drillable = false, editField = null, onSaved }) {
  const [openName, setOpenName] = useState(null);
  const tc = rows.reduce((a, r) => a + r.count, 0);
  const tw = rows.reduce((a, r) => a + r.workers, 0);
  return (
    <div className="border-2 border-slate-900 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 border-b-2 border-slate-900 bg-ncc-navy px-4 py-2 text-white">
        {icon}<div className="industrial-label">{title}</div>
        <div className="ml-auto font-mono text-xs text-ncc-gold">{rows.length} group{rows.length === 1 ? "" : "s"}</div>
      </div>
      <div className="max-h-96 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-100">
            <tr>
              <th className="px-4 py-2 text-left industrial-label">Name</th>
              <th className="px-4 py-2 text-right industrial-label">Records</th>
              <th className="px-4 py-2 text-right industrial-label">Workers</th>
              {drillable && <th className="px-4 py-2 text-right industrial-label">Detail</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={drillable ? 4 : 3} className="p-6 text-center font-mono text-slate-500">No data for the selected range.</td></tr>
            ) : rows.map((r) => (
              <React.Fragment key={r.name}>
                <tr className="border-t border-slate-200 hover:bg-slate-50">
                  <td className="px-4 py-2 font-heading font-bold text-slate-900">{r.name}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.count}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.workers}</td>
                  {drillable && (
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => setOpenName(openName === r.name ? null : r.name)} data-testid={`drill-${testid}-${r.name}`}
                        className="border border-slate-900 bg-white px-2 py-0.5 industrial-label hover:bg-ncc-gold">
                        {openName === r.name ? "Hide" : "View"}
                      </button>
                    </td>
                  )}
                </tr>
                {drillable && openName === r.name && (
                  <tr className="bg-slate-50">
                    <td colSpan={4} className="px-4 py-2">
                      <table className="w-full text-xs">
                        <thead><tr className="text-left industrial-label text-slate-600">
                          <th className="py-1 pr-2">Contractor / Person</th>
                          <th className="py-1 px-2">Mobile</th>
                          {editField === "village" && <th className="py-1 px-2">Village</th>}
                          <th className="py-1 px-2">City</th>
                          <th className="py-1 px-2">State</th>
                          <th className="py-1 px-2 text-right">Workers</th>
                        </tr></thead>
                        <tbody>
                          {(r.contractors || []).length === 0 ? (
                            <tr><td colSpan={editField === "village" ? 6 : 5} className="py-2 text-center font-mono text-slate-500">No contractors.</td></tr>
                          ) : (r.contractors || []).map((p) => (
                            <tr key={p.id} className="border-t border-slate-200">
                              <td className="py-1 pr-2 font-bold">{p.name}</td>
                              <td className="py-1 px-2 font-mono">{p.contact_phone || "—"}</td>
                              {editField === "village" && (
                                <td className="py-1 px-2"><InlineGeoEditor p={p} field="village" onSaved={onSaved} /></td>
                              )}
                              <td className="py-1 px-2">
                                {editField === "city" ? <InlineGeoEditor p={p} field="city" onSaved={onSaved} /> : (p.city || "—")}
                              </td>
                              <td className="py-1 px-2">
                                {editField === "state" ? <InlineGeoEditor p={p} field="state" onSaved={onSaved} /> : (p.state || "—")}
                              </td>
                              <td className="py-1 px-2 text-right font-mono">{p.workers}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="sticky bottom-0 bg-ncc-gold/20">
              <tr className="border-t-2 border-slate-900">
                <td className="px-4 py-2 industrial-label">Total</td>
                <td className="px-4 py-2 text-right font-mono font-bold">{tc}</td>
                <td className="px-4 py-2 text-right font-mono font-bold">{tw}</td>
                {drillable && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent = "bg-white", testid }) {
  return (
    <div className={`border-2 border-slate-900 px-4 py-3 ${accent}`} data-testid={testid}>
      <div className="industrial-label opacity-75">{label}</div>
      <div className="mt-1 font-heading text-2xl font-black">{value}</div>
    </div>
  );
}

// ---------- 1. Workforce report ----------
function WorkforceReport() {
  const { can } = useAuth();
  const [segment, setSegment] = useState("contractor");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = { segment };
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const r = await api.get("/reports/workforce", { params });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [segment, from, to]);

  return (
    <div data-testid="workforce-report">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="industrial-label text-slate-500">Segment</label>
            <div className="mt-1"><SegmentToggle value={segment} onChange={setSegment} testidPrefix="workforce-seg" /></div>
          </div>
          <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} onClear={() => { setFrom(""); setTo(""); }} />
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Records (approved)" value={data?.totals?.records ?? (loading ? "…" : 0)} testid="wf-kpi-records" accent="bg-white" />
        <Kpi label="Total Workers" value={data?.totals?.workers ?? (loading ? "…" : 0)} testid="wf-kpi-workers" accent={segment === "payroll" ? "bg-blueprint text-white" : "bg-ncc-gold"} />
        <Kpi label="Trade Buckets" value={data?.by_trade?.length ?? 0} testid="wf-kpi-trades" />
        <Kpi label="State Buckets" value={data?.by_state?.length ?? 0} testid="wf-kpi-states" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <BreakdownTable title="Trade Wise (click View for contractors)" rows={data?.by_trade ?? []} icon={<BarChart3 className="h-4 w-4" />} testid="wf-trade" drillable />
        <BreakdownTable title="Village Wise" rows={data?.by_village ?? []} icon={<MapPin className="h-4 w-4" />} testid="wf-village" drillable editField={can("edit_contractors") ? "village" : null} onSaved={load} />
        <BreakdownTable title="City Wise" rows={data?.by_city ?? []} icon={<MapPin className="h-4 w-4" />} testid="wf-city" drillable editField={can("edit_contractors") ? "city" : null} onSaved={load} />
        <BreakdownTable title="State Wise" rows={data?.by_state ?? []} icon={<MapPin className="h-4 w-4" />} testid="wf-state" drillable editField={can("edit_contractors") ? "state" : null} onSaved={load} />
      </div>

      <ContractorWise segment={segment} records={data?.records || []} loading={loading} />
    </div>
  );
}

function ContractorWise({ segment, records, loading }) {
  const [search, setSearch] = useState("");
  const label = segment === "contractor" ? "Contractor-Wise List" : "Payroll-Wise List";
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) =>
      [r.name, r.contact_phone, r.city, r.state, r.village, (r.trades || []).join(" ")]
        .join(" ").toLowerCase().includes(q)
    );
  }, [records, search]);

  return (
    <div className="mt-6 border-2 border-slate-900 bg-white" data-testid="wf-contractor-list">
      <div className="flex flex-wrap items-center gap-2 border-b-2 border-slate-900 bg-ncc-navy px-4 py-2 text-white">
        <Users className="h-4 w-4" />
        <div className="industrial-label">{label}</div>
        <div className="font-mono text-xs text-ncc-gold">{filtered.length} / {records.length}</div>
        <div className="ml-auto">
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, mobile, city…"
            data-testid="wf-contractor-search"
            className="border border-slate-300 bg-white px-3 py-1 font-mono text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-ncc-gold"
          />
        </div>
      </div>
      <div className="max-h-[500px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-100">
            <tr>
              <th className="px-4 py-2 text-left industrial-label">Name</th>
              <th className="px-4 py-2 text-left industrial-label">Mobile</th>
              <th className="px-4 py-2 text-left industrial-label">City</th>
              <th className="px-4 py-2 text-left industrial-label">State</th>
              <th className="px-4 py-2 text-left industrial-label">Trades</th>
              <th className="px-4 py-2 text-right industrial-label">Crew</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center font-mono text-slate-500">{loading ? "Loading…" : "No records match."}</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`wf-contractor-row-${r.id}`}>
                <td className="px-4 py-2 font-heading font-bold text-slate-900">{r.name}</td>
                <td className="px-4 py-2 font-mono">{r.contact_phone || "—"}</td>
                <td className="px-4 py-2">{r.city || "—"}</td>
                <td className="px-4 py-2">{r.state || "—"}</td>
                <td className="px-4 py-2">{(r.trades || []).join(", ") || "—"}</td>
                <td className="px-4 py-2 text-right font-mono">{r.crew_size}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="sticky bottom-0 bg-ncc-gold/20">
              <tr className="border-t-2 border-slate-900">
                <td colSpan={5} className="px-4 py-2 industrial-label">Total</td>
                <td className="px-4 py-2 text-right font-mono font-bold">{filtered.reduce((a, r) => a + (r.crew_size || 0), 0)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ---------- 2. Funnel report ----------
function FunnelReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const r = await api.get("/reports/funnel", { params });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const counts = data?.counts || { pending: 0, approved: 0, rejected: 0, total: 0 };
  const byDay = data?.by_day || [];
  const byAdmin = data?.by_admin || [];

  return (
    <div data-testid="funnel-report">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} onClear={() => { setFrom(""); setTo(""); }} />
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Total Registrations" value={counts.total} testid="fn-kpi-total" />
        <Kpi label="Approved" value={counts.approved} accent="bg-ncc-gold" testid="fn-kpi-approved" />
        <Kpi label="Pending" value={counts.pending} testid="fn-kpi-pending" />
        <Kpi label="Rejected" value={counts.rejected} testid="fn-kpi-rejected" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="border-2 border-slate-900 bg-white" data-testid="fn-by-day">
          <div className="border-b-2 border-slate-900 bg-ncc-navy px-4 py-2 text-white industrial-label">Registrations by day</div>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left industrial-label">Date</th>
                  <th className="px-4 py-2 text-right industrial-label">New</th>
                  <th className="px-4 py-2 text-right industrial-label">Approved</th>
                  <th className="px-4 py-2 text-right industrial-label">Rejected</th>
                </tr>
              </thead>
              <tbody>
                {byDay.length === 0 ? (
                  <tr><td colSpan={4} className="p-6 text-center font-mono text-slate-500">{loading ? "Loading…" : "No data."}</td></tr>
                ) : byDay.map((r) => (
                  <tr key={r.date} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono">{r.date}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.registered}</td>
                    <td className="px-4 py-2 text-right font-mono text-emerald-700">{r.approved}</td>
                    <td className="px-4 py-2 text-right font-mono text-red-700">{r.rejected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border-2 border-slate-900 bg-white" data-testid="fn-by-admin">
          <div className="border-b-2 border-slate-900 bg-ncc-navy px-4 py-2 text-white industrial-label">Admin performance</div>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left industrial-label">Admin</th>
                  <th className="px-4 py-2 text-right industrial-label">Approved</th>
                  <th className="px-4 py-2 text-right industrial-label">Rejected</th>
                  <th className="px-4 py-2 text-right industrial-label">Avg Approval (hrs)</th>
                </tr>
              </thead>
              <tbody>
                {byAdmin.length === 0 ? (
                  <tr><td colSpan={4} className="p-6 text-center font-mono text-slate-500">{loading ? "Loading…" : "Audit data appears only for status changes made after this feature was added."}</td></tr>
                ) : byAdmin.map((r) => (
                  <tr key={r.admin_id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="font-heading font-bold text-slate-900">{r.name || "—"}</div>
                      <div className="font-mono text-xs text-slate-500">{r.email || ""}</div>
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-emerald-700">{r.approved_count}</td>
                    <td className="px-4 py-2 text-right font-mono text-red-700">{r.rejected_count}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.avg_approval_hours == null ? "—" : r.avg_approval_hours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- 3. NCC Sites report ----------
function NccSitesReport() {
  const { can } = useAuth();
  const canManage = can("manage_masters");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openSite, setOpenSite] = useState(null);
  const [editingSite, setEditingSite] = useState(null); // original site name being edited
  const [editValue, setEditValue] = useState("");
  const [savingSite, setSavingSite] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const r = await api.get("/reports/ncc-sites", { params });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  function startEdit(siteName) {
    setEditingSite(siteName);
    setEditValue(siteName);
  }
  function cancelEdit() {
    setEditingSite(null);
    setEditValue("");
  }
  async function saveRename(oldName) {
    const newName = editValue.trim();
    if (!newName) { toast.error("New site name cannot be empty"); return; }
    if (newName === oldName) { cancelEdit(); return; }
    setSavingSite(true);
    try {
      const r = await api.post("/admin/ncc-sites/rename", { old_name: oldName, new_name: newName });
      const n = r.data.updated_contractors ?? 0;
      if (n > 0) {
        toast.success(`Renamed · ${n} contractor record${n === 1 ? "" : "s"} updated`);
      } else if (r.data.master_updated) {
        toast.success(`Master list renamed · no contractor records had "${oldName}"`);
      } else {
        toast.success(`Renamed · nothing to update`);
      }
      cancelEdit();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally {
      setSavingSite(false);
    }
  }
  async function deleteSite(s) {
    const inUse = s.total || 0;
    let clearFromContractors = false;
    if (inUse > 0) {
      const confirm1 = window.confirm(
        `Site "${s.site}" is used by ${inUse} record(s).\n\n` +
        `OK = Remove site & CLEAR NCC Site Name from those ${inUse} record(s).\n` +
        `Cancel = Abort.`
      );
      if (!confirm1) return;
      clearFromContractors = true;
    } else {
      if (!window.confirm(`Remove site "${s.site}" from the masters list?`)) return;
    }
    try {
      await api.delete("/admin/ncc-sites/by-name", {
        params: { name: s.site, clear_from_contractors: clearFromContractors },
      });
      toast.success(`Site "${s.site}" removed`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  }

  const sites = data?.sites || [];
  const totals = useMemo(() => sites.reduce((a, s) => ({
    total: a.total + s.total, crew: a.crew + s.total_crew,
    approved: a.approved + s.approved, pending: a.pending + s.pending, rejected: a.rejected + s.rejected,
  }), { total: 0, crew: 0, approved: 0, pending: 0, rejected: 0 }), [sites]);

  return (
    <div data-testid="ncc-sites-report">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} onClear={() => { setFrom(""); setTo(""); }} />
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="NCC Sites" value={sites.length} testid="sites-kpi-count" />
        <Kpi label="Total Records" value={totals.total} testid="sites-kpi-records" accent="bg-ncc-gold" />
        <Kpi label="Total Crew" value={totals.crew} testid="sites-kpi-crew" accent="bg-blueprint text-white" />
        <Kpi label="Approved · Pending · Rejected" value={`${totals.approved} · ${totals.pending} · ${totals.rejected}`} testid="sites-kpi-mix" />
      </div>

      <div className="mt-6 border-2 border-slate-900 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-ncc-navy text-white">
            <tr>
              <th className="px-4 py-3 text-left industrial-label">Site</th>
              <th className="px-4 py-3 text-right industrial-label">Total</th>
              <th className="px-4 py-3 text-right industrial-label">Contractors</th>
              <th className="px-4 py-3 text-right industrial-label">Payroll</th>
              <th className="px-4 py-3 text-right industrial-label">Approved</th>
              <th className="px-4 py-3 text-right industrial-label">Pending</th>
              <th className="px-4 py-3 text-right industrial-label">Rejected</th>
              <th className="px-4 py-3 text-right industrial-label">Total Crew</th>
              <th className="px-4 py-3 text-right industrial-label">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sites.length === 0 ? (
              <tr><td colSpan={9} className="p-6 text-center font-mono text-slate-500">{loading ? "Loading…" : "No NCC site assignments found in this range."}</td></tr>
            ) : sites.map((s) => (
              <React.Fragment key={s.site}>
                <tr className="border-t border-slate-200 hover:bg-slate-50">
                  <td className="px-4 py-2 font-heading font-bold text-slate-900">
                    {editingSite === s.site ? (
                      <div className="flex items-center gap-2">
                        <input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveRename(s.site); if (e.key === "Escape") cancelEdit(); }}
                          autoFocus
                          disabled={savingSite}
                          data-testid={`sites-edit-input-${s.site}`}
                          className="w-full max-w-md border-2 border-ncc-navy bg-white px-2 py-1 font-mono text-xs focus:outline-none"
                        />
                        <button onClick={() => saveRename(s.site)} disabled={savingSite}
                          data-testid={`sites-save-${s.site}`}
                          className="border border-ncc-navy bg-emerald-500 px-1.5 py-1 text-white hover:bg-emerald-600 disabled:opacity-60">
                          {savingSite ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        </button>
                        <button onClick={cancelEdit} disabled={savingSite}
                          data-testid={`sites-cancel-${s.site}`}
                          className="border border-slate-300 bg-white px-1.5 py-1 text-slate-700 hover:bg-slate-100">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      s.site
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{s.total}</td>
                  <td className="px-4 py-2 text-right font-mono">{s.contractor_count}</td>
                  <td className="px-4 py-2 text-right font-mono">{s.payroll_count}</td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-700">{s.approved}</td>
                  <td className="px-4 py-2 text-right font-mono">{s.pending}</td>
                  <td className="px-4 py-2 text-right font-mono text-red-700">{s.rejected}</td>
                  <td className="px-4 py-2 text-right font-mono font-bold">{s.total_crew}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setOpenSite(openSite === s.site ? null : s.site)} data-testid={`sites-toggle-${s.site}`}
                        className="border border-slate-900 bg-white px-2 py-1 industrial-label hover:bg-ncc-gold">{openSite === s.site ? "Hide" : "View"}</button>
                      {canManage && editingSite !== s.site && (
                        <>
                          <button onClick={() => startEdit(s.site)} data-testid={`sites-edit-${s.site}`}
                            title="Rename · cascades to all records"
                            className="border border-slate-900 bg-white p-1.5 text-ncc-navy hover:bg-ncc-tint">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => deleteSite(s)} data-testid={`sites-delete-${s.site}`}
                            title="Remove site"
                            className="border border-red-300 bg-white p-1.5 text-red-600 hover:bg-red-50">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {openSite === s.site && (
                  <tr className="bg-slate-50">
                    <td colSpan={9} className="px-4 py-3">
                      <table className="w-full text-xs">
                        <thead><tr className="text-left industrial-label text-slate-600">
                          <th className="py-1 pr-2">Name</th><th className="py-1 px-2">Type</th><th className="py-1 px-2">Status</th>
                          <th className="py-1 px-2 text-right">Crew</th><th className="py-1 px-2">Trades</th><th className="py-1 px-2">Location</th><th className="py-1 px-2">Phone</th>
                        </tr></thead>
                        <tbody>
                          {s.records.map((r) => (
                            <tr key={r.id} className="border-t border-slate-200">
                              <td className="py-1 pr-2 font-bold">{r.name}</td>
                              <td className="py-1 px-2">{r.is_payroll ? "Payroll" : "Contractor"}</td>
                              <td className="py-1 px-2">{r.status}</td>
                              <td className="py-1 px-2 text-right font-mono">{r.crew_size}</td>
                              <td className="py-1 px-2">{(r.trades || []).join(", ")}</td>
                              <td className="py-1 px-2">{[r.city, r.state].filter(Boolean).join(", ")}</td>
                              <td className="py-1 px-2 font-mono">{r.phone}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- 4. Ready report ----------
function ReadyReport() {
  const [segment, setSegment] = useState("contractor");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/reports/ready-to-mobilize", { params: { segment } });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [segment]);

  const rows = data?.rows || [];
  const label = segment === "contractor" ? "Ready to Mobilize" : "Ready to Join";

  return (
    <div data-testid="ready-report">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <label className="industrial-label text-slate-500">Segment</label>
          <div className="mt-1"><SegmentToggle value={segment} onChange={setSegment} testidPrefix="ready-seg" /></div>
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <Kpi label={`${label} — Records`} value={data?.totals?.records ?? (loading ? "…" : 0)} accent="bg-emerald-100" testid="ready-kpi-records" />
        <Kpi label="Total Workers Available" value={data?.totals?.workers ?? (loading ? "…" : 0)} accent="bg-emerald-200" testid="ready-kpi-workers" />
        <Kpi label="Trade Buckets" value={data?.by_trade?.length ?? 0} testid="ready-kpi-trades" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <BreakdownTable title="By Trade" rows={data?.by_trade ?? []} icon={<BarChart3 className="h-4 w-4" />} testid="ready-by-trade" />
        <BreakdownTable title="By City" rows={data?.by_city ?? []} icon={<MapPin className="h-4 w-4" />} testid="ready-by-city" />
        <BreakdownTable title="By State" rows={data?.by_state ?? []} icon={<MapPin className="h-4 w-4" />} testid="ready-by-state" />
      </div>

      <div className="mt-6 border-2 border-slate-900 bg-white" data-testid="ready-table">
        <div className="border-b-2 border-slate-900 bg-ncc-navy px-4 py-2 text-white industrial-label">{label} — full list</div>
        <div className="max-h-[500px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-100">
              <tr>
                <th className="px-4 py-2 text-left industrial-label">Name</th>
                <th className="px-4 py-2 text-left industrial-label">Phone</th>
                <th className="px-4 py-2 text-left industrial-label">Trades</th>
                <th className="px-4 py-2 text-right industrial-label">Crew</th>
                <th className="px-4 py-2 text-right industrial-label">Ready Nos</th>
                <th className="px-4 py-2 text-left industrial-label">Ready Date</th>
                <th className="px-4 py-2 text-left industrial-label">Location</th>
                <th className="px-4 py-2 text-left industrial-label">Signaled</th>
                <th className="px-4 py-2 text-center industrial-label">Responded</th>
                <th className="px-4 py-2 text-left industrial-label">Remarks</th>
                <th className="px-4 py-2 text-center industrial-label">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={11} className="p-6 text-center font-mono text-slate-500">{loading ? "Loading…" : `No ${label.toLowerCase()} signals pending.`}</td></tr>
              ) : rows.map((d) => (
                <ReadyRow key={d.id} row={d} onRemove={() => {
                  setData((prev) => prev ? { ...prev, rows: prev.rows.filter((r) => r.id !== d.id), totals: { ...prev.totals, records: Math.max(0, (prev.totals?.records || 1) - 1), workers: Math.max(0, (prev.totals?.workers || 0) - (d.crew_size || 0)) } } : prev);
                }} onChange={(patch) => {
                  setData((prev) => prev ? { ...prev, rows: prev.rows.map((r) => r.id === d.id ? { ...r, ...patch } : r) } : prev);
                }} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Editable row for the Ready report: persists "Responded" toggle + "Remarks" inline + Delete
function ReadyRow({ row, onChange, onRemove }) {
  const [responded, setResponded] = useState(!!row.ready_responded);
  const [remarks, setRemarks] = useState(row.ready_remarks || "");
  const [savingResp, setSavingResp] = useState(false);
  const [savingRemarks, setSavingRemarks] = useState(false);

  async function patch(payload) {
    try {
      const r = await api.patch(`/admin/ready-signals/${row.id}/response`, payload);
      onChange?.({ ...payload, ...(r.data || {}) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
      throw err;
    }
  }

  async function toggleResponded(next) {
    setSavingResp(true);
    const prev = responded;
    setResponded(next);
    try {
      await patch({ responded: next });
    } catch {
      setResponded(prev);
    } finally {
      setSavingResp(false);
    }
  }

  async function saveRemarks() {
    if ((remarks || "") === (row.ready_remarks || "")) return;
    setSavingRemarks(true);
    try {
      await patch({ remarks });
    } finally {
      setSavingRemarks(false);
    }
  }

  return (
    <tr className="border-t border-slate-200 hover:bg-slate-50">
      <td className="px-4 py-2 font-heading font-bold">{row.name}</td>
      <td className="px-4 py-2 font-mono">{row.contact_phone}</td>
      <td className="px-4 py-2">{(row.trades || []).join(", ")}</td>
      <td className="px-4 py-2 text-right font-mono">{row.crew_size}</td>
      <td className="px-4 py-2 text-right font-mono" data-testid={`ready-nos-${row.id}`}>
        {row.ready_nos ? <b className="text-ncc-navy">{row.ready_nos}</b> : <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-2 font-mono text-xs" data-testid={`ready-date-${row.id}`}>
        {row.ready_date || <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-2">{[row.city, row.state].filter(Boolean).join(", ")}</td>
      <td className="px-4 py-2 font-mono text-xs">{(row.ready_signaled_at || "").slice(0, 19).replace("T", " ")}</td>
      <td className="px-4 py-2 text-center">
        <button
          type="button"
          onClick={() => toggleResponded(!responded)}
          disabled={savingResp}
          data-testid={`ready-responded-toggle-${row.id}`}
          aria-pressed={responded}
          className={`inline-flex items-center gap-1 rounded-full border-2 px-3 py-1 text-xs font-bold transition-colors ${
            responded
              ? "border-emerald-700 bg-emerald-100 text-emerald-800"
              : "border-slate-300 bg-white text-slate-500 hover:bg-slate-100"
          } disabled:opacity-60`}
        >
          <span className={`inline-block h-2 w-2 rounded-full ${responded ? "bg-emerald-600" : "bg-slate-400"}`} />
          {responded ? "Yes" : "No"}
        </button>
      </td>
      <td className="px-4 py-2">
        <input
          type="text"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          onBlur={saveRemarks}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          placeholder="Add remarks…"
          data-testid={`ready-remarks-input-${row.id}`}
          className={`w-full min-w-[200px] rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:border-ncc-navy focus:outline-none focus:ring-1 focus:ring-ncc-navy ${savingRemarks ? "opacity-60" : ""}`}
        />
      </td>
      <td className="px-4 py-2 text-center">
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm(`Clear the Ready-to-Mobilize signal for "${row.name}"? They can press Ready again later — this only removes them from this list and the dashboard widget.`)) return;
            try {
              await api.delete(`/admin/ready-signals/${row.id}`);
              toast.success(`${row.name} removed from Ready list`);
              onRemove?.();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Could not remove");
            }
          }}
          data-testid={`ready-delete-${row.id}`}
          aria-label={`Remove ${row.name} from Ready list`}
          className="inline-flex items-center justify-center rounded border-2 border-red-300 bg-white p-1.5 text-red-600 transition-colors hover:border-red-700 hover:bg-red-50"
          title="Clear this Ready-to-Mobilize signal"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}


// ============================================================
// APPROVAL QUEUES panel (moved from top navbar in v1.7)
// Wraps the 4 existing approval pages inside a sub-toggle so users
// can flip between Contractor / Payroll / Workmen / Pay-Roster
// queues without leaving Reports. Live pending-count badges are
// preserved on each toggle button — poll `silent: true` to avoid
// warming-up toasts.
// ============================================================
function ApprovalQueuesPanel() {
  const { user } = useAuth();
  const can = React.useCallback((p) => user?.is_super || (user?.permissions || []).includes(p), [user]);
  const options = React.useMemo(() => [
    can("approve_contractors") && { id: "contractor", label: "Contractors", perm: "approve_contractors" },
    can("approve_payroll")     && { id: "payroll",    label: "Payroll",     perm: "approve_payroll" },
    (can("approve_workmen") || can("approve_medical")) && { id: "workmen", label: "Workmen", perm: "approve_workmen" },
    (can("approve_payroll_roster") || can("approve_medical")) && { id: "pay-roster", label: "Pay-Roster", perm: "approve_payroll_roster" },
  ].filter(Boolean), [can]);

  const [activeQueue, setActiveQueue] = React.useState(options[0]?.id || "contractor");
  const [counts, setCounts] = React.useState({ contractor: 0, payroll: 0, workmen: 0, "pay-roster": 0 });

  React.useEffect(() => {
    let cancelled = false;
    const fetchCounts = async () => {
      const next = { ...counts };
      try {
        if (can("approve_contractors")) {
          const r = await api.get("/contractors", { silent: true, params: { status: "pending", is_payroll: false, include_pending: true } });
          next.contractor = (r.data || []).length;
        }
        if (can("approve_payroll")) {
          const r = await api.get("/contractors", { silent: true, params: { status: "pending", is_payroll: true, include_pending: true } });
          next.payroll = (r.data || []).length;
        }
        if (can("approve_workmen") || can("approve_medical")) {
          const r = await api.get("/workmen/pending", { silent: true });
          next.workmen = (r.data || []).length;
        }
        if (can("approve_payroll_roster") || can("approve_medical")) {
          const r = await api.get("/pay-roster/pending", { silent: true });
          next["pay-roster"] = (r.data || []).length;
        }
        if (!cancelled) setCounts(next);
      } catch { /* silent */ }
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 60000);
    return () => { cancelled = true; clearInterval(t); };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [user]);

  if (options.length === 0) {
    return (
      <div className="border-2 border-slate-900 bg-white p-8 shadow-brutal-sm text-center">
        <div className="industrial-label text-[11px] text-slate-500">// Reports · Approvals</div>
        <p className="mt-2 text-sm text-slate-700">Your role has no approval permissions. Contact a Super Admin to gain access.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="approvals-report-panel">
      <div className="border-b-2 border-slate-900 pb-3">
        <div className="industrial-label text-[10px] text-slate-500">// Reports · Approval Queues</div>
        <h2 className="font-heading text-2xl font-black text-ncc-navy">Approval Queues</h2>
        <p className="text-xs text-slate-600 mt-1">All pending items across Contractor, Payroll, Workmen, and Pay-Roster approval workflows — consolidated in one place.</p>
      </div>
      {/* Sub-toggle with live count badges */}
      <div className="flex flex-wrap items-center gap-1 border-2 border-slate-900 bg-white p-1 shadow-brutal-sm">
        {options.map((o) => {
          const isActive = activeQueue === o.id;
          const count = counts[o.id] || 0;
          return (
            <button
              key={o.id}
              type="button"
              data-testid={`approvals-tab-${o.id}`}
              onClick={() => setActiveQueue(o.id)}
              className={`relative inline-flex items-center gap-2 border-2 px-3 py-2 industrial-label text-xs transition-all ${
                isActive
                  ? "border-slate-900 bg-ncc-navy text-white shadow-brutal-sm"
                  : "border-transparent bg-white text-slate-700 hover:bg-slate-100"
              }`}
            >
              {o.label}
              {count > 0 && (
                <span className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full border px-1 font-mono text-[10px] font-bold ${isActive ? "border-white bg-white text-ncc-navy" : "border-red-700 bg-red-600 text-white"}`} data-testid={`approvals-tab-${o.id}-count`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* Embedded queue page — reuses the existing approval components */}
      <div className="border-2 border-slate-900 bg-white shadow-brutal-sm">
        {activeQueue === "contractor" && <ContractorApprovalsPage mode="contractor" embedded />}
        {activeQueue === "payroll" && <ContractorApprovalsPage mode="payroll" embedded />}
        {activeQueue === "workmen" && <WorkmenApprovalsPage embedded />}
        {activeQueue === "pay-roster" && <PayRosterApprovalsPage embedded />}
      </div>
    </div>
  );
}
